'use client';

// OpenUI Lang chat library CSS — must load on the client, before any
// <Renderer> mount, so Card borders / Tag pills / FollowUpBlock buttons
// pick up their styling. Same import the OpenUI vercel-ai-chat example
// ships at the top of its surface.
import '@openuidev/react-ui/components.css';
// Lab-local overrides: stretch Carousel items to fill the chat column when
// there are only a few, and hide the chevron scroll arrows that otherwise
// appear floating over cards even when content fits.
import './openui-overrides.css';

// Lab variant of /chat-view. Verbatim copy from apps/web/components/chat-view.tsx
// (sessions DB, durable workflow, eval mode, model selector, telemetry, auth
// modal, usage limits, drafts — ALL preserved) with two additions on top:
//
//   1. OpenUI Lang chat library Renderer for assistant text — when no /chat
//      tool fired and no dashboard tool fired, the model's text is parsed as
//      OpenUI Lang DSL and rendered via the chat library (Card / Tags /
//      Charts / FollowUpBlock / etc.). Markdown via MessageResponse remains
//      the fallback for tool-heavy turns and for parse failures (caught by
//      the RendererBoundary).
//
//   2. Recharts max-update-depth fix at the wrapper layer — see
//      lib/genui-charts.tsx, where `useIsStreaming()` guards chart mounting
//      so partial chart-data tokens during streaming don't drive recharts
//      into its internal infinite update loop.
//
// This file is intended to be the future /chat surface once vetted in the
// lab. /api/chat workflow + sessions / durability stay; OpenUI Lang is
// additive.

import { Component, forwardRef, memo, useState, useEffect, useCallback, useImperativeHandle, useRef, useMemo, type ReactNode } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type ChatStatus, type PrepareSendMessagesRequest, type UIMessage } from 'ai';
import { WorkflowChatTransport } from '@workflow/ai';
import { Renderer, BuiltinActionType, type ActionEvent as OpenUIActionEvent } from '@openuidev/react-lang';
import { ThemeProvider } from '@openuidev/react-ui';
import { galexiaChatLibrary } from '@/lib/genui-chat-library';
import { resolveTheme } from '@/lib/openui-theme';
import { DashboardPanel, type StreamingDashboard } from './dashboard-panel';
import { useMobileSidebar } from '@/components/client-layout';
import { bumpVisualizationVersion } from '@/lib/visualization-refresh-bus';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { VIDEO_CATEGORY_PRESETS, type VideoCategoryId } from '@/lib/video-category-presets';
import { COMPOSER_MODES, getComposerMode, type ComposerModeId } from '@/lib/composer-modes';
import { SlideTemplateGallery, SlideTemplatesButton } from '@/components/slides/slide-template-picker';
import {
  CaretDown,
  ChalkboardTeacher,
  ChatCircleDots,
  Check,
  ClockCounterClockwise,
  Cpu,
  FilmSlate,
  Globe,
  Copy,
  ArrowsClockwise,
  GitBranch,
  ArrowsOutSimple,
  LockSimple,
  FileText,
  Graph,
  Images,
  MagnifyingGlass,
  MusicNotes,
  PresentationChart,
  RocketLaunch,
  Sparkle,
  VideoCamera,
  SquaresFour,
  FrameCorners,
  File as FileIcon,
  FilePdf,
  WarningCircle,
  X,
  Microphone,
  ThumbsUp,
  ThumbsDown,
  ArrowBendDownRight,
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';
import { useVoiceRecorder } from '@/lib/voice-input';
import { useAuth, useUser, SignInButton } from '@clerk/nextjs';
import { GalexiaLogo } from '@/components/galexia-logo';
import { HomeFeatureCarousel } from '@/components/home-feature-carousel';
import { FirstVisitDemoModal } from '@/components/first-visit-demo-modal';
import { ConnectionRequestCard } from '@/components/connection-request-card';
import { AskUserQuestionCard } from '@/components/ask-user-question-card';
import { Button } from '@/components/ui/button';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useSession } from '@/contexts/session-context';

// AI Elements
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
  ConversationScrollButton,
} from '@/components/ai-elements/conversation';
import { ActiveTurnIndicator } from '@/components/ai-elements/active-turn-indicator';
import { ChatErrorPanel } from '@/components/ai-elements/chat-error-panel';
import { formatChatError, type ChatErrorInfo } from '@/lib/chat-error-format';
// URL swap onto a just-created session. MUST stay this call and not a raw
// window.history.replaceState: apps/spa aliases the module so TanStack Router
// does not re-match the URL and unmount this component mid-stream. Under Next
// the implementation IS history.replaceState — see lib/chat-url.ts.
import { replaceChatUrl } from '@/lib/chat-url';
import {
  Message,
  MessageContent,
  MessageResponse,
  MessageActions,
  MessageAction,
} from '@/components/ai-elements/message';
import {
  PromptInput,
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputAttachment,
  PromptInputAttachments,
  PromptInputBody,
  PromptInputButton,
  PromptInputHeader,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputTools,
} from '@/components/ai-elements/prompt-input';
import {
  CHAT_UPLOAD_ACCEPT,
  CHAT_UPLOAD_SUPPORTED_LABEL,
  uploadFileToMediaLibrary,
} from '@/lib/media-upload-client';
import {
  Source,
  Sources,
  SourcesContent,
  SourcesTrigger,
} from '@/components/ai-elements/sources';
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning';
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
  summarizeToolInput,
} from '@/components/ai-elements/tool';
import { getToolLabel } from '@/lib/tool-display';
import { Loader } from '@/components/ai-elements/loader';
import { ToolLoader, ToolError } from '@/components/ai-elements/tool-loader';

// Visualization registry and components
import { visualizationRegistry } from '@/components/visualizations';
import { InlineHtmlBlock } from '@/components/inline-html-block';
import { ViewVisualizationToolOutput } from '@/components/visualizations/view-visualization-renderer';
import { WebSearchToolOutput } from '@/components/web-search-result';
import { ChatShareButton } from '@/components/chat-share-button';
import {
  getVisualizationDisplayTitle,
  resolveVisualizationPresentationMode,
  type VisualizationPresentationMode,
  ToolVisualizationRenderer,
} from '@/components/tool-visualization-renderer';
import { VisualizationArtifactPanel, ArtifactTypeIcon, getArtifactAccent } from '@/components/visualization-artifact-panel';
import { getVisualizationTypeLabel } from '@/lib/visualization-type-labels';
import { SavedVisualizationPreview } from '@/components/saved-visualization-preview';
import { StreamingSvgPreview } from '@/components/visualizations/streaming-svg-preview';
import { StreamingHtmlPreview } from '@/components/visualizations/streaming-html-preview';
import { StreamingToolInput } from '@/components/ai-elements/streaming-tool-input';
import { pickStreamingToolBody } from '@/lib/streaming-tool-input';
import {
  readToolPartInput,
  readToolPartRawInputText,
  readToolPartErrorText,
} from '@/lib/tool-part-input';
import { PendingNarratedVideo } from '@/components/visualizations/pending-narrated-video';
import { CompletedToolsGroup, isUtilityTool } from '@/components/ai-elements/completed-tools-group';
import { humanizeToolName, isCollapsibleWorkTool } from '@/lib/timeline-synthesis';
import { groupMessagesIntoTurns, type HistoryMessage, type HistoryTurn } from '@/lib/session-history';
import { HistoryPanel, type HistoryArtifactChip } from './history-panel';
import { resolveDelegatedToolDisplay } from '@/lib/capabilities/display';
import { getWorkspaceOpenRequestKey } from '@/lib/workspace-open-request';
import { hasTurnErrorMarker, parseTurnError, stripTurnErrorMarkersFromMessages } from '@/lib/turn-error';
import {
  ModelSelector,
  ModelSelectorContent,
  ModelSelectorEmpty,
  ModelSelectorGroup,
  ModelSelectorInput,
  ModelSelectorItem,
  ModelSelectorList,
  ModelSelectorName,
  ModelSelectorTrigger,
} from '@/components/ai-elements/model-selector';
import { captureProductTelemetry } from '@/lib/product-telemetry';
import { deriveArtifactPanelState } from '@/lib/artifact-panel-state';
import {
  onAutoSystemMessage,
  formatSystemMessage,
  parseSystemMessageKind,
  type AutoSystemMessageEvent,
} from '@/lib/auto-system-message-bus';
import { appendSystemTime, stripSystemTime } from '@/lib/system-time-tag';
import { clearStoredWorkflowRunId, getStoredWorkflowRunId, setStoredWorkflowRunId } from '@/lib/workflow-run-storage';
import { getModelOptionById, type ModelOption } from '@/lib/model-options';
import { useModalA11y } from '@/lib/use-modal-a11y';
import { useDocumentTitle } from '@/lib/use-document-title';

// Quick start suggestions for the workspace home. They should advertise the
// whole system: chat, browser, files, code, media, dashboards, and workflows.
const ALL_SUGGESTIONS = [
  'Research SambaNova, capture website screenshots, and draft a 30-second ad video',
  'Open a browser, inspect navismartai.com, and build a marketing brief',
  'Analyze this PDF and turn it into a study workspace with diagrams',
  'Create a product launch kit: images, video script, landing-page copy, and checklist',
  'Build a React dashboard prototype for renewable energy trends',
  'Compare iPhone, Pixel, and Galaxy flagships with charts and buying advice',
  'Plan a customer support workflow with tools, automations, and escalation rules',
  'Generate brand visuals and a storyboard from my uploaded logo',
  'Summarize my documents into an executive brief and action plan',
  'Use the browser to research competitors and create a feature matrix',
  'Make a narrated explainer video from this topic and source material',
  'Help me choose a laptop for video editing under $2000',
  'Build a startup metrics dashboard: MRR, churn, CAC, runway',
  'Create a lesson on DNA replication with diagrams, images, and quiz cards',
  'Draft a project plan, then turn it into tasks, docs, and a status dashboard',
  'Write and test a small TypeScript tool for cleaning CSV files',
];

const RETRY_FROM_FAILURE_TEXT = [
  '<system_message kind="retry-from-failure">',
  'The previous generation failed after some work may have completed.',
  'Continue from the current conversation state. Treat completed text, artifacts, and tool outputs above as already done.',
  'Do not repeat successful tool calls unless their outputs are missing, invalid, or explicitly need to be refreshed.',
  'If the failing step can be retried directly, retry only that step and then finish the user request.',
  '</system_message>',
].join('\n');

/**
 * Typewriter rotating placeholder for the homepage input. Types a prompt out
 * char-by-char, pauses, erases, moves to the next. Pauses entirely when
 * `active` is false (e.g. user has typed something) so we don't compete with
 * their input. Subtle premium touch — surfaces example prompts without a
 * banner and disappears the moment they engage.
 *
 * Honours prefers-reduced-motion: instead of the keystroke-by-keystroke
 * animation, the placeholder swaps fully-formed prompts every ~5s so reduced-
 * motion users still see the breadth of suggestions without constant typing
 * activity.
 */
function useRotatingPlaceholder(prompts: string[], active: boolean): string {
  const [text, setText] = useState('');
  const reducedMotion = useReducedMotion();
  const stateRef = useRef({
    promptIdx: 0,
    charIdx: 0,
    phase: 'typing' as 'typing' | 'pausing' | 'erasing',
    pauseTicks: 0,
  });
  useEffect(() => {
    if (!active || prompts.length === 0) return;

    if (reducedMotion) {
      // Static rotation: full prompt swaps every 5s with no typing animation.
      let i = 0;
      const id = setInterval(() => {
        i = (i + 1) % prompts.length;
        setText(prompts[i]);
      }, 5000);
      return () => clearInterval(id);
    }

    const id = setInterval(() => {
      const s = stateRef.current;
      const current = prompts[s.promptIdx % prompts.length];
      if (s.phase === 'typing') {
        if (s.charIdx < current.length) {
          s.charIdx += 1;
          setText(current.slice(0, s.charIdx));
        } else {
          s.phase = 'pausing';
          s.pauseTicks = 0;
        }
      } else if (s.phase === 'pausing') {
        s.pauseTicks += 1;
        // Shorter pause (~990ms) so longer prompts cycle faster — original
        // 30-tick pause stacked with the longer suggestion list shipped
        // earlier made the typewriter feel stalled on long prompts.
        if (s.pauseTicks > 18) s.phase = 'erasing';
      } else {
        if (s.charIdx > 0) {
          // Erase 3× faster than typing — humans erase fast, and the read
          // value of the typewriter is the typed-out moment, not the
          // unwind. Keeps total cycle ~6s for a 60-char prompt vs ~9s.
          s.charIdx = Math.max(0, s.charIdx - 3);
          setText(current.slice(0, s.charIdx));
        } else {
          s.promptIdx = (s.promptIdx + 1) % prompts.length;
          s.phase = 'typing';
        }
      }
    }, 55);
    return () => clearInterval(id);
  }, [active, prompts, reducedMotion]);
  return active ? (text || prompts[0] || '') : '';
}

function pickSuggestions(n = 4): string[] {
  // Fisher-Yates with deterministic seed per page-load — server/client agree
  // because the seed is generated once on mount via React state.
  const out = [...ALL_SUGGESTIONS];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.slice(0, n);
}

const omniModeOptions = [
  {
    id: 'chat',
    label: 'AI Chat',
    icon: ChatCircleDots,
    colorClass: 'text-[#005dff]',
    prefill: '',
  },
  {
    id: 'apps',
    label: 'AI Apps',
    icon: SquaresFour,
    colorClass: 'text-[#00c8ff]',
    prefill: 'Build an app for ',
  },
  {
    id: 'slides',
    label: 'AI Slides',
    icon: PresentationChart,
    colorClass: 'text-[#e6b800]',
    prefill: 'Create a slide deck about ',
  },
  {
    id: 'images',
    label: 'AI Images',
    icon: Images,
    colorClass: 'text-[#d02bff]',
    prefill: 'Generate images for ',
  },
  {
    id: 'videos',
    label: 'AI Videos',
    icon: VideoCamera,
    colorClass: 'text-[#00c8ff]',
    prefill: 'Create a cinematic video about ',
    badge: 'NEW',
    proOnly: true,
  },
  {
    id: 'graphs',
    label: 'AI Graphs',
    icon: Graph,
    colorClass: 'text-[#ff9800]',
    prefill: 'Build charts and graphs for ',
    badge: 'NEW',
  },
  {
    id: 'music',
    label: 'AI Music',
    icon: MusicNotes,
    colorClass: 'text-[#00d95a]',
    prefill: 'Generate music for ',
    badge: 'NEW',
  },
];

const CHAT_MODEL_STORAGE_KEY = 'galexia-chat-model';
const USE_WORKFLOW_CHAT_TRANSPORT = process.env.NEXT_PUBLIC_CHAT_EXECUTION_MODE === 'workflow';

interface EvalConfig {
  /** Custom API endpoint for streaming */
  api: string;
  /** Extra body params (e.g., { model }) */
  body?: Record<string, unknown>;
  /** Unique useChat ID (prevents state collision between multiple instances) */
  chatId?: string;
  /** Auto-send this prompt on mount */
  autoSend?: string;
}

interface ArtifactCandidate {
  key: string;
  toolName: string;
  input: Record<string, unknown>;
  output?: unknown;
  visualizationId?: string;
  presentation: VisualizationPresentationMode;
}

function supportsArtifactPanel(toolName: string, input?: unknown, output?: unknown) {
  const delegated = resolveDelegatedToolDisplay(toolName, input, output);
  const effectiveToolName = delegated.toolName;
  return (
    effectiveToolName === 'createBrowser' ||
    effectiveToolName === 'browserLiveView' ||
    effectiveToolName === 'browserNavigate' ||
    effectiveToolName === 'browserScroll' ||
    effectiveToolName === 'html' ||
    effectiveToolName === 'htmlNative' ||
    effectiveToolName === 'generateImage' ||
    effectiveToolName === 'generateImageBatch' ||
    effectiveToolName === 'p5' ||
    effectiveToolName === 'remotion' ||
    effectiveToolName === 'hyperframes' ||
    effectiveToolName === 'scene3d' ||
    effectiveToolName === 'svg' ||
    effectiveToolName === 'createNarratedVideo' ||
    effectiveToolName === 'createParallelNarratedVideo' ||
    effectiveToolName === 'sandboxPreviewUrl' ||
    effectiveToolName === 'generateSpeech' ||
    effectiveToolName === 'generateNarrationPlan' ||
    effectiveToolName === 'generateMusic' ||
    effectiveToolName === 'generateSoundEffect' ||
    effectiveToolName === 'generateSoundEffectBatch' ||
    effectiveToolName === 'viewMediaAsset' ||
    effectiveToolName === 'saveMediaAssetFromUrl' ||
    effectiveToolName === 'promoteGeneratedMediaToAsset' ||
    effectiveToolName === 'sandboxExec' ||
    effectiveToolName === 'sandboxCodeRun' ||
    effectiveToolName === 'sandboxSessionExec' ||
    effectiveToolName === 'sandboxSessionLogs' ||
    effectiveToolName === 'sandboxListFiles' ||
    effectiveToolName === 'sandboxReadFile' ||
    effectiveToolName === 'browserListFiles' ||
    effectiveToolName === 'browserReadFile' ||
    effectiveToolName === 'readGalexiaFile' ||
    effectiveToolName === 'readGalexiaFileLines' ||
    effectiveToolName === 'listMyDocuments' ||
    effectiveToolName === 'viewDocument' ||
    effectiveToolName === 'getVisualizationSource' ||
    effectiveToolName === 'readVisualizationLines' ||
    effectiveToolName === 'grepVisualization'
  );
}

const VISUALIZATION_TOOL_NAMES = new Set([
  'comparisonTable',
  'timeline',
  'conceptMap',
  'cycleDiagram',
  'flowchart',
  'graph',
  'hierarchy',
  'html',
  'mermaid',
  'molecule3d',
  'p5',
  'remotion',
  'hyperframes',
  'scene3d',
  'svg',
  'generateImage',
  'createBrowser',
  'browserLiveView',
  'browserNavigate',
  'browserScroll',
]);

function isVisualizationToolName(toolName: string, input?: unknown, output?: unknown) {
  const delegated = resolveDelegatedToolDisplay(toolName, input, output);
  return VISUALIZATION_TOOL_NAMES.has(delegated.toolName);
}

// Human headlines for auto-system-message kinds (lib/auto-system-message-bus.ts)
// — the compact chip rendered inline for the model-facing <system_message>
// events. Unknown kinds fall back to humanizeToolName rather than a raw
// kebab-case slug (CHAT_UI_DESIGN_REVIEW.md §4 item 6).
const SYSTEM_MESSAGE_KIND_LABELS: Record<string, string> = {
  'render-complete': 'Render finished',
  'render-error': 'Render failed',
  'render-ready': 'Render ready',
};

interface ChatViewProps {
  /** Session ID - if null, this is a new chat and will create session on first message */
  sessionId?: string | null;
  /** When true, hides input and disables sending (for admin viewing) */
  readOnly?: boolean;
  /** When true, fetches session with admin privileges (bypasses ownership check) */
  adminView?: boolean;
  /** Pre-loaded messages (e.g. from server component for shared chats). Skips API fetch when provided. */
  initialMessages?: Array<{ id: string; role: string; parts: unknown[]; metadata?: unknown }>;
  /** Eval mode config — uses custom API, auto-sends prompt, skips session/auth/usage logic */
  evalConfig?: EvalConfig;
}

type PendingClientTtftMeasurement = {
  clientRequestId: string;
  startedAt: number;
  messageCountAtSubmit: number;
  sessionId: string | null;
  chatModel: string | null;
  route: string;
  executionMode: 'direct' | 'workflow';
};

function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function hasVisibleAssistantOutput(message: UIMessage) {
  if (message.role !== 'assistant') return false;

  return message.parts.some((part) => {
    const candidate = part as unknown as Record<string, unknown>;
    const type = typeof candidate.type === 'string' ? candidate.type : '';

    if (type === 'text') {
      return typeof candidate.text === 'string' && candidate.text.trim().length > 0;
    }

    if (type === 'reasoning') {
      const text = candidate.text ?? candidate.reasoning;
      return typeof text === 'string' && text.trim().length > 0;
    }

    return (
      type.startsWith('tool-') ||
      type.startsWith('data-') ||
      type === 'source-url' ||
      type === 'source-document' ||
      type === 'file'
    );
  });
}

function sendClientPerformanceMetric(payload: {
  clientRequestId: string;
  sessionId: string | null;
  metricType: 'chat_client_ttft';
  valueMs: number;
  route: string;
  chatModel: string | null;
  executionMode: 'direct' | 'workflow';
  metadata?: Record<string, unknown>;
}) {
  const body = JSON.stringify(payload);
  const url = '/api/analytics/client-metrics';

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const sent = navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    if (sent) return;
  }

  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch((error) => {
    console.warn('[ClientMetrics] Failed to report client TTFT:', error);
  });
}

// ---- OpenUI Lang plumbing ---------------------------------------------------

const DASHBOARD_TOOL_NAMES = new Set<string>([
  'create_dashboard', 'update_dashboard', 'show_dashboard',
]);

// Heuristic: does this text look like OpenUI Lang DSL (so the Renderer can
// parse it), or is it normal markdown prose (so MessageResponse / Streamdown
// should handle it)?
//
// Rule: text qualifies as OpenUI Lang only if it OPENS with a DSL signature.
// `root = Card(...)`, `root = Stack(...)`, or a DSL function call as the
// first non-whitespace token. We DON'T sniff arbitrary mentions of `Card(`
// or `Tags(` mid-prose because the model regularly writes prose like
// "I called Card() to..." which would false-positive and route real
// markdown into the OpenUI Lang parser (which then renders nothing).
//
// `/api/chat` workflow currently ships the markdown system prompt, so 99%
// of turns will hit the markdown path. Once we wire the OpenUI Lang
// preamble into the workflow, this discriminator will cleanly catch the
// DSL turns the model actually emits.
const OPENUI_LANG_OPENERS = new Set<string>([
  'Card', 'Stack', 'Container', 'Page', 'Wrapper',
]);
// Split an assistant text part into a prose prefix (markdown, may be empty)
// and a DSL block (may be null). The model is supposed to follow the
// inline-mode rules from the prompt but in practice emits four shapes:
//
//   1. Pure prose          → { prose: text, dsl: null }
//   2. Pure DSL            → { prose: '',   dsl: text }
//   3. Fenced DSL in prose → { prose: 'pre + post', dsl: fenceContent }
//   4. Raw DSL after prose → { prose: 'pre',  dsl: 'root = ...' }
//
// (4) is the trap that broke earlier turns — model wrote "Let me use Tabs:"
// then a raw `root = Card(...)` block. We scan for the first line that
// starts with `root\s*=` (or a known opener like Card(...)/Stack(...)),
// treat content before as prose, content from there as DSL.
//
// The renderer then renders both: markdown for prose, OpenUI Lang Renderer
// for the DSL. Both share one Message bubble so it reads as a single turn.
function splitProseAndDSL(text: string): { prose: string; dsl: string | null } {
  if (!text || !text.trim()) return { prose: '', dsl: null };

  // Shape 3 — fenced DSL ```openui-lang / ```openui / ```dsl ... ```
  const labeledFence = text.match(/```(?:openui-lang|openui|dsl)\s*\n([\s\S]*?)\n```/);
  if (labeledFence && labeledFence.index != null) {
    const before = text.slice(0, labeledFence.index).trim();
    const after = text.slice(labeledFence.index + labeledFence[0].length).trim();
    return {
      prose: [before, after].filter(Boolean).join('\n\n'),
      dsl: labeledFence[1].trim(),
    };
  }

  // Shape 3 (no label) — ``` ... root = ... ```
  const looseFence = text.match(/```\s*\n(root\s*=[\s\S]*?)\n```/);
  if (looseFence && looseFence.index != null) {
    const before = text.slice(0, looseFence.index).trim();
    const after = text.slice(looseFence.index + looseFence[0].length).trim();
    return {
      prose: [before, after].filter(Boolean).join('\n\n'),
      dsl: looseFence[1].trim(),
    };
  }

  // Shapes 2 / 4 — find first line starting with `root = ...`. Treat
  // everything from that line onward as DSL; everything before is prose.
  // Multiline regex with line-start anchor.
  const dslStart = text.match(/^[ \t]*root\s*=/m);
  if (dslStart && dslStart.index != null) {
    const prose = text.slice(0, dslStart.index).trim();
    const dsl = text.slice(dslStart.index).trim();
    return { prose, dsl };
  }

  // Shape 2 alt — text opens directly with a known root component
  // (Card(...) / Stack(...)) without `root =` assignment. Rare but valid.
  const trimmed = text.trim();
  const bareOpener = trimmed.match(/^([A-Z][A-Za-z]+)\s*\(/);
  if (bareOpener && OPENUI_LANG_OPENERS.has(bareOpener[1])) {
    return { prose: '', dsl: trimmed };
  }

  // Shape 1 — pure prose
  return { prose: text, dsl: null };
}

// Per-message error boundary around the OpenUI Lang Renderer. Recharts
// (used by the chat library's BarChart/LineChart/etc.) can hit "Maximum
// update depth exceeded" on partial or malformed series data while the
// model is still streaming. The chart wrappers in lib/genui-charts.tsx
// already gate mounting on `useIsStreaming()` to PREVENT this — but if
// anything escapes (parser exceptions, runtime errors), this boundary
// catches them so the rest of the chat surface stays interactive.
class RendererBoundary extends Component<
  { children: ReactNode; fallbackText: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error('[GenUIChat] OpenUI Lang Renderer crashed:', error);
  }
  render() {
    if (this.state.error) {
      const truncated = this.props.fallbackText.length > 800
        ? `${this.props.fallbackText.slice(0, 780)}…`
        : this.props.fallbackText;
      return (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] px-4 py-3">
          <p className="text-sm font-medium text-amber-200/95">
            Couldn&apos;t render this response visually
          </p>
          <p className="mt-1 text-[12px] text-amber-100/70 leading-relaxed">
            The OpenUI components had trouble rendering — usually means the
            model emitted partial data while streaming. Raw response below;
            click Retry to regenerate.
          </p>
          <pre className="mt-3 max-h-40 overflow-auto rounded-md bg-black/30 px-3 py-2 text-[11px] font-mono text-amber-50/80 whitespace-pre-wrap break-words">
            {truncated}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

// Memoized wrapper around the OpenUI Lang Renderer. The message list re-runs on
// every streaming token, and the Renderer (charts/cards) is expensive. Its props
// here are all value-stable for a completed turn — `response` is a string
// (value-compared), `isStreaming` a boolean, `onContinue` a stable useCallback —
// so React.memo skips re-rendering finished DSL turns while a later message
// streams. `library` and the onAction closure live inside the memo boundary so
// they never enter the prop comparison. memo is strictly safe: any changed prop
// still triggers a re-render, so it never shows stale content.
const MemoizedDslRenderer = memo(function MemoizedDslRenderer({
  response,
  isStreaming,
  onContinue,
}: {
  response: string;
  isStreaming: boolean;
  onContinue: (text: string) => void;
}) {
  return (
    <Renderer
      response={response}
      library={galexiaChatLibrary}
      isStreaming={isStreaming}
      onAction={(event: OpenUIActionEvent) => {
        if (
          event.type === BuiltinActionType.ContinueConversation &&
          event.humanFriendlyMessage
        ) {
          onContinue(event.humanFriendlyMessage);
        }
      }}
    />
  );
});

// =============================================================================
// ChatMessageRow — one message turn, memoized.
//
// The message list re-renders on every streaming flush. Before this
// extraction the whole ~1000-line map body re-ran for EVERY message per
// flush; combined with slow dev renders that outlasted the chunk interval,
// React counted 50+ nested store updates and threw "Maximum update depth
// exceeded", killing the generation. All props are referentially stable for
// completed turns (the SDK only swaps the streaming message object), so
// React.memo skips them entirely — per-flush render cost is O(streaming
// message), not O(conversation).
// =============================================================================

interface ChatMessageRowProps {
  message: UIMessage;
  isLastMessage: boolean;
  prevIsAssistant: boolean;
  nextIsAssistant: boolean;
  status: ChatStatus;
  currentSessionId: string | null;
  readOnly: boolean;
  activeArtifact: ArtifactCandidate | null;
  isArtifactOpen: boolean;
  isDesktop: boolean;
  activeDashboardId: string | null;
  setActiveDashboardId: (id: string | null) => void;
  openArtifact: (candidate: ArtifactCandidate, source?: string) => void;
  closeArtifact: (reason?: string) => void;
  handleSubmit: (message: PromptInputMessage) => void | Promise<void>;
  handleRendererContinue: (text: string) => void;
  handleRetryVizError: (info: { toolName: string; error: string; code: string }) => void;
  handleRetryFromFailure: () => void;
  regenerate: () => void;
  /** Conversation branching — fork a NEW chat from this user message. Draft is
   *  the message's text, pre-filled (not auto-sent) into the branch composer. */
  handleBranch?: (messageId: string, draftText: string) => void;
}

// Exported for the build-timeline fixture replay test
// (components/ai-elements/__tests__/build-timeline-replay.test.tsx) — it
// renders this row over realistic heavy sessions to pin the aggregation
// behavior. Not part of the public surface otherwise.
export const ChatMessageRow = memo(function ChatMessageRow({
  message,
  isLastMessage,
  prevIsAssistant,
  nextIsAssistant,
  status,
  currentSessionId,
  readOnly,
  activeArtifact,
  isArtifactOpen,
  isDesktop,
  activeDashboardId,
  setActiveDashboardId,
  openArtifact,
  closeArtifact,
  handleSubmit,
  handleRendererContinue,
  handleRetryVizError,
  handleRetryFromFailure,
  regenerate,
  handleBranch,
}: ChatMessageRowProps) {
  const sources = message.parts.filter((part) => part.type === 'source-url');

  // Thumbs up/down feedback state for THIS message — local to the row (no
  // dedicated feedback table; POSTs to /api/feedback which records it as an
  // analytics event, see apps/server/src/routes/feedback.ts). `null` = not
  // rated yet; set optimistically on click, reverted silently on failure.
  const [feedbackRating, setFeedbackRating] = useState<'up' | 'down' | null>(null);
  const [feedbackSending, setFeedbackSending] = useState(false);
  const submitFeedback = useCallback((rating: 'up' | 'down') => {
    if (feedbackSending || !currentSessionId) return;
    const previous = feedbackRating;
    setFeedbackRating(rating);
    setFeedbackSending(true);
    fetch('/api/feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messageId: message.id, sessionId: currentSessionId, rating }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Feedback request failed (${res.status})`);
      })
      .catch((error) => {
        console.error('[ChatMessageRow] Failed to send feedback:', error);
        setFeedbackRating(previous);
      })
      .finally(() => setFeedbackSending(false));
  }, [feedbackSending, feedbackRating, currentSessionId, message.id]);

  // Post-turn follow-up suggestion chips (feature: chat-view follow-ups).
  // Populated on the assistant message's `metadata.followUpSuggestions` by
  // the server AFTER finalize (see chat/finalize.ts + generate-metadata.ts's
  // generateFollowUpSuggestions) — never derived client-side. Only shown
  // once the turn has actually finished (not mid-stream) on the last message.
  const followUpSuggestions = (message.metadata as { followUpSuggestions?: unknown } | undefined)?.followUpSuggestions;
  const showFollowUps =
    !readOnly &&
    isLastMessage &&
    message.role === 'assistant' &&
    status !== 'streaming' &&
    status !== 'submitted' &&
    Array.isArray(followUpSuggestions) &&
    followUpSuggestions.every((s): s is string => typeof s === 'string') &&
    followUpSuggestions.length > 0;

  // Per-turn dashboard-tool detection (used by the dashboard pill
  // rendering elsewhere — kept here so the OpenUI Lang Renderer
  // path can opt out for dashboard turns where text is
  // intentionally a plain status caption).
  const turnToolNames = message.parts
    .filter((p) => p.type.startsWith('tool-'))
    .map((p) => p.type.replace('tool-', ''));
  const dashboardToolUsed = turnToolNames.some((n) => DASHBOARD_TOOL_NAMES.has(n));

  // Detect whether this assistant turn will render OpenUI Lang
  // DSL via the Renderer. When it does, the inner Card from the
  // DSL is the visual frame for the message — wrapping it in
  // the chat-view's `bg-muted/40 px-4 py-3 rounded-2xl` outer
  // turn wrapper creates a doubled Card-in-Card look that
  // breaks proportions. Flatten the outer wrapper for those
  // turns so the OpenUI Lang Card stands alone.
  const turnRendersDSL =
    message.role === 'assistant' &&
    !dashboardToolUsed &&
    message.parts.some(
      (p) => p.type === 'text' && splitProseAndDSL(p.text).dsl !== null
    );

  // Pre-compute runs of utility-tool parts (plus the reasoning
  // parts that sit between them with no user-facing text in
  // between) so they collapse into one ChainOfThought-style
  // group in the chat. Walk rule:
  //   • Completed utility tool → join current group
  //   • Reasoning part → join current group ONLY if a utility
  //     tool has already started the group (otherwise the
  //     reasoning is standalone and renders inline as before)
  //   • Anything else (text, dashboard tool, viz tool, file)
  //     breaks the chain and the group is committed
  type GroupTool = { kind: 'tool'; partIndex: number; toolName: string; input: unknown; output: unknown; errored: boolean; hasCard: boolean };
  type GroupReasoning = { kind: 'reasoning'; partIndex: number; text: string };
  type GroupNote = { kind: 'note'; partIndex: number; text: string };
  type GroupItem = GroupTool | GroupReasoning | GroupNote;

  const utilityGroups: Array<GroupItem[]> = [];
  {
    let current: GroupItem[] = [];
    let currentHasTool = false;
    const commit = () => {
      if (currentHasTool) utilityGroups.push(current);
      current = [];
      currentHasTool = false;
    };
    message.parts.forEach((p, i) => {
      if (p.type === 'reasoning') {
        // Only fold reasoning into a group that already has a
        // tool — reasoning at the very start of a turn (before
        // any tool) stays standalone so the inline trigger
        // still appears in its natural position.
        if (currentHasTool) {
          const text = (p as { text?: string }).text ?? '';
          if (text.trim()) {
            current.push({ kind: 'reasoning', partIndex: i, text });
          }
        }
        return;
      }
      // Skip parts that don't carry user-visible inline content:
      //   • step-start — AI SDK v6 lifecycle marker emitted
      //     between every tool / reasoning step (the bug-
      //     causer in the bitcoin-vs-apple screenshot)
      //   • source-url — webSearch citations, rendered
      //     separately at the top via <Sources>
      // Without this skip they'd commit the current group on
      // every step boundary, splitting one natural chain-of-
      // thought into many tiny groups.
      // (step-finish isn't in AI SDK v6's part type union, so
      // we don't need to handle it — TS would reject the
      // comparison, which is what failed the Vercel build.)
      if (p.type === 'step-start' || p.type === 'source-url') {
        return;
      }
      if (!p.type.startsWith('tool-')) {
        commit();
        return;
      }
      const toolName = p.type.replace('tool-', '');
      const state = (p as { state?: string }).state;
      const completed = state === 'output-available' || state === 'output-error';
      // rawInput-aware: an `output-error` part carries undefined `input` (see
      // lib/tool-part-input.ts), and every helper below reads input fields.
      const input = readToolPartInput(p as { input?: unknown; rawInput?: unknown });
      const output = (p as { output?: unknown }).output;
      const delegated = resolveDelegatedToolDisplay(toolName, input, output);
      // A completed part joins the timeline when it's either a quiet
      // utility tool (search, memory, skills…) or agent WORK whose
      // standalone card is machinery noise — sandbox exec runs, file
      // reads/writes, source greps (the "terminal log" problem). Work
      // tools keep their full original card INSIDE the expanded entry
      // (hasCard), utility tools keep the compact JSON drilldown.
      // Exception: if this exact part is the artifact currently docked
      // in the side panel, it stays standalone so the docked-state chip
      // (and its "Show inline" affordance) remains visible in the chat.
      const isDockedArtifact =
        isDesktop && isArtifactOpen && activeArtifact?.key === `${message.id}-${i}`;
      const isWorkCard = isCollapsibleWorkTool(toolName, input, output);
      if (completed && !isDockedArtifact && (isUtilityTool(toolName, input, output) || isWorkCard)) {
        current.push({
          kind: 'tool',
          partIndex: i,
          toolName: delegated.toolName,
          input: delegated.input ?? input,
          output: delegated.output ?? output,
          errored: state === 'output-error',
          hasCard: isWorkCard,
        });
        currentHasTool = true;
      } else {
        commit();
      }
    });
    commit();
  }

  // Second pass: merge ADJACENT groups when nothing genuinely visible sits
  // between them — either literally nothing (only step-start/source-url
  // markers), or a short (<150 char), plain narration with no fences/DSL/
  // system markers. Without this, one continuous run of work reads as N
  // sibling timeline entries whenever the model narrates between calls
  // (CHAT_UI_DESIGN_REVIEW.md §4 item 2 — "Captured screenshot" ×3 as three
  // rows, separated only by one-sentence narrations). The folded text is
  // kept, not dropped — it renders as a quiet "note" step inside the merged
  // entry's expansion. A SUBSTANTIAL narrative (>= the threshold) still
  // breaks the run: that's a real topic change, not filler, and the model
  // stopping to explain something meaningful should still read as two
  // separate moments.
  const FOLD_TEXT_MAX_CHARS = 150;
  const foldedNotePartIndex = new Set<number>();
  const mergedUtilityGroups: GroupItem[][] = [];
  utilityGroups.forEach((group) => {
    const prev = mergedUtilityGroups[mergedUtilityGroups.length - 1];
    if (prev) {
      const prevEnd = prev[prev.length - 1].partIndex;
      const nextStart = group[0].partIndex;
      const between = message.parts.slice(prevEnd + 1, nextStart);
      let foldable = between.length > 0;
      const noteChunks: string[] = [];
      for (const p of between) {
        if (p.type === 'step-start' || p.type === 'source-url') continue;
        if (p.type !== 'text') {
          foldable = false;
          break;
        }
        const text = stripSystemTime((p as { text?: string }).text ?? '').trim();
        if (!text) continue;
        if (
          text.includes('```') ||
          text.includes('<system_message') ||
          text.includes('<clientside_error_report') ||
          parseTurnError(text) ||
          text.length >= FOLD_TEXT_MAX_CHARS
        ) {
          foldable = false;
          break;
        }
        noteChunks.push(text);
      }
      if (foldable) {
        between.forEach((p, idx) => {
          if (p.type === 'text') foldedNotePartIndex.add(prevEnd + 1 + idx);
        });
        if (noteChunks.length > 0) {
          prev.push({ kind: 'note', partIndex: prevEnd + 1, text: noteChunks.join(' ') });
        }
        prev.push(...group);
        return;
      }
    }
    mergedUtilityGroups.push(group);
  });

  // Map each part index → group info. Used by both the inline
  // tool-render (skip subsequent members; render the group
  // wrapper at the first member's position) and the inline
  // reasoning-render (skip when part is folded into a group).
  const groupByPartIndex = new Map<number, { members: GroupItem[]; isFirst: boolean }>();
  mergedUtilityGroups.forEach((group) => {
    group.forEach((m, idx) => {
      groupByPartIndex.set(m.partIndex, { members: group, isFirst: idx === 0 });
    });
  });

  // ---------------------------------------------------------------------------
  // renderToolCard — one tool part rendered as its full standalone card (the
  // pre-timeline behavior). Called directly from the parts map for tools that
  // stay visible in the chat, and reused INSIDE the expanded timeline entry so
  // aggregated work steps reveal their exact original renderers (terminal
  // cards, file viewers, artifact expand affordances) — nothing is rebuilt.
  // ---------------------------------------------------------------------------
  const renderToolCard = (part: UIMessage['parts'][number], partIndex: number): ReactNode => {
    const toolPart = part as {
      type: string;
      state: 'input-streaming' | 'input-available' | 'output-available' | 'output-error';
      input?: unknown;
      /** Raw, unparsed arguments the SDK kept when the model's JSON was malformed. */
      rawInput?: unknown;
      output?: unknown;
      errorText?: string;
      visualizationId?: string;
    };
    const toolName = toolPart.type.replace('tool-', '');
    const output = toolPart.output as { success?: boolean; error?: string; visualizationId?: string } | undefined;
    // NEVER read `toolPart.input` directly: a malformed/truncated model call
    // leaves it undefined with the payload stranded in `rawInput` (see
    // lib/tool-part-input.ts — prod incident 2026-07-29).
    const input = readToolPartInput(toolPart);
    const delegated = resolveDelegatedToolDisplay(toolName, input, output);
    const effectiveToolName = delegated.toolName;
    const effectiveInput = (delegated.input ?? input) as Record<string, unknown> | undefined;
    const effectiveOutput = (delegated.output ?? output) as { success?: boolean; error?: string; visualizationId?: string } | undefined;
    // visualizationId comes from tool output (primary) or backfilled part property (fallback for saved messages)
    const vizId = effectiveOutput?.visualizationId || output?.visualizationId || toolPart.visualizationId;

    // A call the SDK rejected (unparseable / schema-invalid arguments) never
    // ran, so it has NO output — only `errorText` and possibly a truncated
    // `rawInput`. Render it as a normal failed-tool card FIRST, before any
    // renderer that assumes a shaped input/output (prod incident 2026-07-29,
    // where such a part fell through the whole chain into a blank card).
    if (toolPart.state === 'output-error') {
      const rawArgs = readToolPartRawInputText(toolPart);
      return (
        <div key={`${message.id}-${partIndex}`}>
          <ToolError
            toolName={effectiveToolName}
            error={
              rawArgs
                ? `${readToolPartErrorText(toolPart)}\n\nArguments received (truncated):\n${rawArgs}`
                : readToolPartErrorText(toolPart)
            }
          />
        </div>
      );
    }

    // viewVisualization — always use minimal inline indicator (success or failure)
    if (effectiveToolName === 'viewVisualization') {
      return (
        <div key={`${message.id}-${partIndex}`} className="my-1">
          <ViewVisualizationToolOutput output={effectiveOutput} />
        </div>
      );
    }

    // Dashboard tools — inline tile that lets the user re-open the
    // side panel after they've closed it. Tool itself runs in the
    // workflow and triggers setActiveDashboardId via the effect at
    // ~line 1325; this tile is a passive re-open affordance.
    if (effectiveToolName === 'create_dashboard' || effectiveToolName === 'update_dashboard' || effectiveToolName === 'show_dashboard') {
      const dashOutput = effectiveOutput as { id?: string; name?: string } | undefined;
      const dashInput = effectiveInput as { name?: string; title?: string } | undefined;
      const dashId = dashOutput?.id;
      const dashName = dashOutput?.name || dashInput?.name || dashInput?.title || 'Dashboard';
      const verb =
        effectiveToolName === 'create_dashboard' ? 'Created dashboard'
        : effectiveToolName === 'update_dashboard' ? 'Updated dashboard'
        : 'Opening dashboard';
      const isActive = dashId !== undefined && activeDashboardId === dashId;
      return (
        <div
          key={`${message.id}-${partIndex}`}
          className="my-3 flex items-center justify-between gap-3 rounded-xl border border-violet-500/25 bg-violet-500/[0.05] px-4 py-3"
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 inline-flex size-9 items-center justify-center rounded-lg border border-violet-500/30 bg-violet-500/15 text-violet-700 dark:text-violet-200">
              <SquaresFour weight="duotone" className="size-4.5" />
            </div>
            <div className="min-w-0">
              {/* Dark-only literals (violet-50/violet-200) were near-invisible
                  in light mode — CHAT_UI_DESIGN_REVIEW.md §4 item 1. */}
              <p className="text-sm font-medium text-violet-900 dark:text-violet-50 truncate">{dashName}</p>
              <p className="text-[10px] font-medium uppercase tracking-[1.5px] text-violet-700/70 dark:text-violet-200/55 mt-0.5">{verb}</p>
            </div>
          </div>
          {dashId && (
            <Button
              variant={isActive ? 'outline' : 'default'}
              size="sm"
              onClick={() => {
                if (isActive) {
                  setActiveDashboardId(null);
                } else {
                  setActiveDashboardId(dashId);
                }
              }}
              className="shrink-0 gap-1.5"
            >
              {isActive ? 'Close' : 'Open'}
            </Button>
          )}
        </div>
      );
    }

    // If tool returned an error, show compact error UI
    if (effectiveOutput && effectiveOutput.success === false && effectiveOutput.error) {
      return (
        <div key={`${message.id}-${partIndex}`}>
          <ToolError toolName={effectiveToolName} error={effectiveOutput.error} />
        </div>
      );
    }

    // If tool is still running/pending, show animated loader.
    // BUT: once status is no longer 'streaming' / 'submitted'
    // (user clicked stop, or stream errored out), the tool
    // part stays in its pending state in the messages array
    // because it never got the closing output-available frame.
    // Without this gate the loader would spin forever for
    // any tool that was mid-flight when stop was hit.
    if (toolPart.state === 'input-streaming' || toolPart.state === 'input-available') {
      const stillStreaming = status === 'streaming' || status === 'submitted';
      if (!stillStreaming) {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/30 border border-border/40 text-xs text-muted-foreground">
            <span className="size-1.5 rounded-full bg-muted-foreground/40" />
            <span>{effectiveToolName} stopped before completing</span>
          </div>
        );
      }
      if (effectiveToolName === 'svg') {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-4">
            <StreamingSvgPreview input={effectiveInput} />
          </div>
        );
      }

      if (effectiveToolName === 'html') {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-4">
            <StreamingHtmlPreview input={effectiveInput} />
          </div>
        );
      }

      // High-value writers (writeFile, sandbox/browser write, htmlNative,
      // remotion, hyperframes, mermaid…) — stream the body field into a live
      // code view so the user watches the file being typed, not a spinner.
      const streamingBody = pickStreamingToolBody(effectiveToolName, effectiveInput ?? input);
      if (streamingBody) {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-4">
            <StreamingToolInput body={streamingBody} />
          </div>
        );
      }

      return (
        <div key={`${message.id}-${partIndex}`}>
          <ToolLoader toolName={effectiveToolName} input={effectiveInput ?? input} />
        </div>
      );
    }

    // generateImage — by default we DO NOT render
    // the result inline. The model is expected to
    // embed the returned image URL inside an OpenUI
    // Lang Card / ImageGallery wherever it wants in
    // the composition. The model can opt back in
    // with input.inline=true for one-off "draw me X"
    // turns where the image IS the entire response.
    if (effectiveToolName === 'generateImage' && effectiveOutput?.success) {
      const imgInput = effectiveInput as { inline?: boolean } | undefined;
      if (!imgInput?.inline && !supportsArtifactPanel(effectiveToolName, effectiveInput, effectiveOutput)) return null;
    }

    // Web search — render results from output
    if (effectiveToolName === 'webSearch' && effectiveOutput) {
      return (
        <div key={`${message.id}-${partIndex}`} className="my-2">
          <WebSearchToolOutput output={effectiveOutput as { success: boolean; query: string; results?: string; error?: string }} />
        </div>
      );
    }

    // suggestInteraction — minimal pill, don't bother the user
    if (effectiveToolName === 'suggestInteraction') {
      return null;
    }

    if (effectiveToolName === 'showWorkspace' && effectiveOutput?.success) {
      return null;
    }

    if (effectiveToolName === 'askUserQuestion' && effectiveOutput) {
      return (
        <AskUserQuestionCard
          key={`${message.id}-${partIndex}`}
          output={effectiveOutput}
          onAnswer={(text) => handleSubmit({ text, files: [] })}
          disabled={!isLastMessage || status === 'streaming' || status === 'submitted'}
        />
      );
    }

    if (effectiveToolName === 'requestConnection' && effectiveOutput) {
      return (
        <ConnectionRequestCard
          key={`${message.id}-${partIndex}`}
          output={effectiveOutput}
          onRespond={(text) => handleSubmit({ text, files: [] })}
        />
      );
    }

    if (
      effectiveToolName === 'getVisualizationSource' &&
      effectiveOutput?.success &&
      !supportsArtifactPanel(effectiveToolName, effectiveInput, effectiveOutput)
    ) {
      return null;
    }

    if (effectiveToolName === 'listVisualizationVersions' && effectiveOutput?.success) {
      const versionOutput = effectiveOutput as {
        versions?: Array<{
          id: string;
          versionNumber: number;
          changeType: string;
          changeSummary?: string | null;
          createdAt: string | Date;
          isActive: boolean;
        }>;
      };

      if (!versionOutput.versions?.length) {
        return null;
      }

      return (
        <div key={`${message.id}-${partIndex}`} className="my-4 rounded-xl border border-border bg-muted/20 p-4">
          <p className="text-sm font-medium">Version history</p>
          <div className="mt-3 space-y-2">
            {versionOutput.versions.map((version) => (
              <div
                key={version.id}
                className="flex flex-col gap-1 rounded-lg border border-border/70 bg-background/70 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">v{version.versionNumber}</span>
                    {version.isActive && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                        Active
                      </span>
                    )}
                  </div>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    {version.changeType}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {version.changeSummary || 'No summary provided'}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (
      (effectiveToolName === 'editVisualization' || effectiveToolName === 'revertVisualizationVersion') &&
      effectiveOutput?.success &&
      effectiveOutput.visualizationId
    ) {
      const versionOutput = effectiveOutput as {
        visualizationId: string;
        versionNumber?: number;
        changeSummary?: string;
        title?: string;
      };

      return (
        <div key={`${message.id}-${partIndex}`} className="my-4 space-y-3">
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium">
                {effectiveToolName === 'editVisualization' ? 'Visualization updated' : 'Visualization reverted'}
              </p>
              <p className="text-sm text-muted-foreground">
                {versionOutput.changeSummary || 'Updated visualization'}
                {typeof versionOutput.versionNumber === 'number' ? ` · v${versionOutput.versionNumber}` : ''}
              </p>
            </div>
          </div>
          <SavedVisualizationPreview
            visualizationId={versionOutput.visualizationId}
            sessionId={currentSessionId}
            onRetryError={handleRetryVizError}
          />
        </div>
      );
    }

    // createNarratedVideo / createParallelNarratedVideo are now routed
    // through the standard artifact-candidate flow below — they're
    // marked as 'artifact-preferred' so the side panel auto-opens
    // with the PendingNarratedVideo card. Inline falls back to a
    // "currently open in workspace" chip when docked, or full
    // PendingNarratedVideo via ToolVisualizationRenderer when the
    // user has the panel closed.

    // createNarratedVideoBatch — N parallel videos. Render N live cards.
    if (effectiveToolName === 'createNarratedVideoBatch' && effectiveOutput?.success) {
      const batchOutput = effectiveOutput as {
        videos?: Array<{
          taskId: string;
          publicAccessToken: string;
          title: string;
          estimatedDurationSec?: number;
        }>;
        count?: number;
      };
      if (batchOutput.videos?.length) {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-2 space-y-2">
            <p className="text-xs text-muted-foreground">
              Generating {batchOutput.videos.length} videos in parallel
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {batchOutput.videos.map((v) => (
                <PendingNarratedVideo
                  key={v.taskId}
                  taskId={v.taskId}
                  publicAccessToken={v.publicAccessToken}
                  title={v.title}
                  estimatedDurationSec={v.estimatedDurationSec}
                />
              ))}
            </div>
          </div>
        );
      }
    }

    // If tool succeeded, render visualization from INPUT (not output)
    if (effectiveOutput?.success && effectiveInput) {
      const typedInput = effectiveInput as Record<string, unknown>;
      const presentation = delegated.presentation ?? resolveVisualizationPresentationMode(effectiveToolName, typedInput);
      const candidate: ArtifactCandidate = {
        key: `${message.id}-${partIndex}`,
        toolName: effectiveToolName,
        input: typedInput,
        output: effectiveOutput,
        visualizationId: vizId,
        presentation,
      };
      const artifactTitle = delegated.title ?? getVisualizationDisplayTitle(effectiveToolName, typedInput);
      const artifactAccent = getArtifactAccent(effectiveToolName);
      const artifactTypeLabel = getVisualizationTypeLabel(effectiveToolName);
      const canOpenArtifact = supportsArtifactPanel(effectiveToolName, typedInput, effectiveOutput);
      const isDockedInArtifact = isDesktop && isArtifactOpen && activeArtifact?.key === candidate.key;
      const artifactAction = canOpenArtifact && !isDockedInArtifact ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openArtifact(candidate, 'inline-expand')}
          className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowsOutSimple weight="bold" className="size-3.5" />
          <span>Expand</span>
        </Button>
      ) : null;

      if (presentation === 'artifact-only') {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-4 rounded-2xl border border-border/70 bg-card/40 p-4 backdrop-blur-sm transition-colors hover:border-border">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`inline-flex size-9 shrink-0 items-center justify-center rounded-xl border ${artifactAccent.chip}`}>
                  <ArtifactTypeIcon toolName={effectiveToolName} iconClass={`size-4.5 ${artifactAccent.icon}`} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium leading-tight">{artifactTitle}</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60">
                    <span className={`size-1.5 rounded-full ${artifactAccent.dot}`} aria-hidden />
                    {artifactTypeLabel}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => openArtifact(candidate, 'artifact-only-expand')}
                className="shrink-0 gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
              >
                <ArrowsOutSimple weight="bold" className="size-4" />
                Open
              </Button>
            </div>
          </div>
        );
      }

      if (isDockedInArtifact) {
        return (
          <div key={`${message.id}-${partIndex}`} className="my-4 rounded-2xl border border-border/70 bg-card/40 p-4 backdrop-blur-sm">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`inline-flex size-9 shrink-0 items-center justify-center rounded-xl border ${artifactAccent.chip}`}>
                  <ArtifactTypeIcon toolName={effectiveToolName} iconClass={`size-4.5 ${artifactAccent.icon}`} />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium leading-tight">{artifactTitle}</p>
                  <p className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground/60">
                    <span className="relative flex size-1.5">
                      <span className={`absolute inline-flex size-full animate-ping rounded-full ${artifactAccent.dot} opacity-70`} />
                      <span className={`relative inline-flex size-1.5 rounded-full ${artifactAccent.dot}`} />
                    </span>
                    Open in workspace
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => closeArtifact('show-inline')}
                className="shrink-0 gap-1.5 whitespace-nowrap border-border/70"
              >
                Show inline
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div key={`${message.id}-${partIndex}`} className="my-4">
          <ToolVisualizationRenderer
            toolName={effectiveToolName}
            input={typedInput}
            output={effectiveOutput}
            visualizationId={vizId}
            sessionId={currentSessionId}
            onRetryError={handleRetryVizError}
            renderActions={artifactAction}
            className={effectiveToolName === 'mermaid' ? 'w-full max-w-full overflow-x-hidden' : undefined}
          />
        </div>
      );
    }
    
    // Fallback tool card: ALWAYS collapsed by default —
    // raw JSON dumps cause layout thrash and read as a
    // mess. The header carries a humanized label + a
    // one-line input summary so the collapsed state is
    // still informative; expanding shows bounded JSON.
    return (
      <Tool key={`${message.id}-${partIndex}`}>
        <ToolHeader
          title={getToolLabel(toolName)}
          summary={summarizeToolInput(toolPart.input)}
          type={toolPart.type as `tool-${string}`}
          state={toolPart.state}
        />
        <ToolContent>
          <ToolInput input={toolPart.input} />
          {(toolPart.state === 'output-available' || toolPart.state === 'output-error') && (
            <ToolOutput
              output={toolPart.output}
              errorText={toolPart.errorText}
            />
          )}
        </ToolContent>
      </Tool>
    );
  };

  return (
    <div
      className={
        message.role === 'assistant'
          ? [
              // Unified bubble for ALL assistant turns — markdown,
              // dashboard captions, AND DSL Cards. The DSL path
              // also adds `lab-turn-dsl` so openui-overrides.css
              // can flatten the inner OpenUI Card chrome (bg /
              // border / padding / radius) — the outer bubble
              // becomes the single visual entity, and the Card
              // contributes only its vertical-stack structure.
              'group/turn bg-muted/40 px-4 py-3',
              turnRendersDSL ? 'lab-turn-dsl' : '',
              // Collapse gap-8 when prev turn is also assistant — same bg merges them visually
              prevIsAssistant ? '-mt-8 border-t border-border/20' : 'rounded-t-2xl',
              nextIsAssistant ? '' : 'rounded-b-2xl',
            ].join(' ')
          : 'group/turn'
      }
    >
      {/* Sources (before message) */}
      {message.role === 'assistant' && sources.length > 0 && (
        <Sources>
          <SourcesTrigger count={sources.length} />
          {sources.map((part, i) => (
            <SourcesContent key={`${message.id}-source-${i}`}>
              <Source href={part.url} title={part.url} />
            </SourcesContent>
          ))}
        </Sources>
      )}

      {/* Message parts */}
      {message.parts.map((part, partIndex) => {
        switch (part.type) {
          case 'text': {
            // Folded into a merged timeline group as a "note" step (see the
            // merge pass above) — skip the standalone render so it doesn't
            // show twice.
            if (foldedNotePartIndex.has(partIndex)) return null;
            // Strip the appended <system_time> tag for rendering.
            // The tag persists in the stored message text so the
            // model sees it on every turn, but the user never
            // does. See lib/system-time-tag.ts.
            const partTextDisplay = stripSystemTime(part.text);
            // User messages with error reports: show compact indicator instead of raw error
            if (message.role === 'user' && partTextDisplay.includes('<clientside_error_report>')) {
              const toolMatch = part.text.match(/Tool: (\w+)/);
              const toolName = toolMatch?.[1] || 'visualization';
              return (
                <Message key={`${message.id}-${partIndex}`} from="user">
                  <MessageContent>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ArrowsClockwise weight="bold" className="size-3.5" />
                      <span>Retrying failed {toolName} render...</span>
                    </div>
                  </MessageContent>
                </Message>
              );
            }

            // Auto-system-message events: compact system chip
            // (mirrors chat-view.tsx; see auto-system-message-bus.ts for the why).
            if (message.role === 'user' && part.text.includes('<system_message')) {
              const kind = parseSystemMessageKind(part.text) ?? 'event';
              const isError = kind.includes('error') || kind.includes('fail');
              // A human headline, not the raw kebab-case kind slug — "system ·
              // render error" read as a terminal log line (CHAT_UI_DESIGN_REVIEW.md
              // §4 item 6: "status synthesis matters most when things break").
              // Errors are red per the review's amber=degraded/red=failed rule;
              // this event is a hard failure, not a warning.
              const kindLabel = SYSTEM_MESSAGE_KIND_LABELS[kind] ?? humanizeToolName(kind);
              return (
                <div
                  key={`${message.id}-${partIndex}`}
                  className={`my-2 flex items-center gap-2 text-[10px] font-medium uppercase tracking-[1.5px] ${isError ? 'text-destructive/80' : 'text-muted-foreground/60'}`}
                >
                  <span className={`inline-block size-1.5 rounded-full ${isError ? 'bg-destructive' : 'bg-violet-500'}`} />
                  <span>{kindLabel}</span>
                </div>
              );
            }

            // Turn-failure marker: render as a compact error banner below the completed content
            const turnError = parseTurnError(part.text);
            if (turnError) {
              const kindConfig = {
                cancelled: { label: 'Stopped', color: 'text-amber-500', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
                timeout:   { label: 'Timed out', color: 'text-orange-500', bg: 'bg-orange-500/10', border: 'border-orange-500/30' },
                error:     { label: 'Generation failed', color: 'text-destructive', bg: 'bg-destructive/10', border: 'border-destructive/30' },
              }[turnError.kind];
              return (
                <div
                  key={`${message.id}-${partIndex}`}
                  className={`mt-2 flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm ${kindConfig.bg} ${kindConfig.border}`}
                >
                  <span className={`flex size-6 shrink-0 items-center justify-center rounded-full ${kindConfig.bg} ${kindConfig.color} font-bold`}>
                    !
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={`text-xs font-semibold ${kindConfig.color}`}>
                      {kindConfig.label}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {turnError.message}
                    </p>
                  </div>
                  {!readOnly && message.role === 'assistant' && isLastMessage && (
                    <button
                      onClick={handleRetryFromFailure}
                      className={`shrink-0 px-2.5 py-1 text-xs font-medium rounded-md border ${kindConfig.border} ${kindConfig.color} hover:bg-background/60 transition-colors`}
                    >
                      Retry
                    </button>
                  )}
                </div>
              );
            }

            // Split text by fenced blocks (json for OpenUI Lang
            // visualizations OR html for inline HTML output) and
            // render in order. Both formats coexist with markdown
            // prose; the model picks whichever fits.
            //
            // Pattern matches BOTH closed fences (`\`\`\`html ... \`\`\``)
            // AND unclosed fences at end-of-stream (`\`\`\`html <partial>$`).
            // The unclosed case is what makes streaming HTML
            // render as it arrives — without it the block sits as
            // raw text until the closing \`\`\` lands.
            const segments: Array<{ type: 'text' | 'viz' | 'html'; content: string; vizData?: { type: string; props: unknown } }> = [];
            const fencedPattern = /```(json|html)\s*([\s\S]*?)(?:\s*```|$)/g;
            let match;
            let lastIndex = 0;

            while ((match = fencedPattern.exec(partTextDisplay)) !== null) {
              // Add text before this match
              if (match.index > lastIndex) {
                const textBefore = partTextDisplay.substring(lastIndex, match.index).trim();
                if (textBefore) {
                  segments.push({ type: 'text', content: textBefore });
                }
              }

              const lang = match[1];
              const body = match[2];

              if (lang === 'html') {
                // Inline HTML output — render in a sandboxed iframe.
                segments.push({ type: 'html', content: body });
              } else {
                // json: try to parse as an OpenUI Lang visualization
                try {
                  const parsed = JSON.parse(body);
                  const componentType = parsed.component || parsed.type;

                  if (componentType && visualizationRegistry[componentType as keyof typeof visualizationRegistry]) {
                    let props = parsed.props ?? parsed.data ?? (() => {
                      const { component, type: t, actions, ...rest } = parsed;
                      return rest;
                    })();

                    // Handle CycleDiagram stages -> steps mapping
                    if (componentType === 'CycleDiagram' && props.stages && !props.steps) {
                      props = { ...props, steps: props.stages };
                    }

                    segments.push({ type: 'viz', content: match[0], vizData: { type: componentType, props } });
                  } else {
                    // Unknown viz type, render as code
                    segments.push({ type: 'text', content: match[0] });
                  }
                } catch {
                  // JSON parse fail, render as code
                  segments.push({ type: 'text', content: match[0] });
                }
              }

              lastIndex = match.index + match[0].length;
            }

            // Add remaining text after last match
            if (lastIndex < partTextDisplay.length) {
              const textAfter = partTextDisplay.substring(lastIndex).trim();
              if (textAfter) {
                segments.push({ type: 'text', content: textAfter });
              }
            }

            // OpenUI Lang chat-library mode: only fires when no
            // OpenUI Lang chat-library mode: only fires when the
            // text actually OPENS with a DSL signature (`root =`
            // or a known root component like Card(...)). Plain
            // markdown prose with bullet lists, tables, bold —
            // even on tool-less turns — falls through to the
            // MessageResponse path below. Dashboard turns
            // intentionally bypass this entirely (their chat
            // text is a plain status caption per the prompt).
            // Split the assistant text into prose (markdown)
            // and DSL (OpenUI Lang). Handles all four shapes
            // the model emits — pure prose, pure DSL, fenced
            // DSL with prose around it, and raw DSL after a
            // prose intro (the trap that previously rendered
            // raw `root = Card(...)` as plain text).
            // If the text contains an inline ```html block,
            // ALWAYS take the segments path. The DSL branch
            // below renders ONLY the DSL and drops everything
            // else, which silently nuked HTML blocks when the
            // model ended a reply with SuggestedQueries(...) or
            // similar trailing DSL. Inline HTML is the primary
            // visual surface now; never lose it to a trailing
            // DSL chip.
            const hasInlineHtmlBlock = /```html[\s\S]/.test(partTextDisplay);
            const split =
              message.role === 'assistant' && !dashboardToolUsed && !hasInlineHtmlBlock
                ? splitProseAndDSL(partTextDisplay)
                : { prose: partTextDisplay, dsl: null as string | null };
            if (split.dsl) {
              // Drop the prose prefix entirely when DSL exists
              // — that prose is the model thinking out loud
              // ("Let me use Tabs instead..."), not user-
              // facing content. The Reasoning collapsible
              // tray (rendered for `type: 'reasoning'` parts)
              // already exposes the model's full chain of
              // thought when the user wants to peek. Showing
              // the prose AND the DSL doubles up the
              // explanation and clutters the response.
              const isThisStreaming =
                status === 'streaming' &&
                partIndex === message.parts.length - 1 &&
                isLastMessage;
              return (
                <Message key={`${message.id}-${partIndex}`} from={message.role} className="max-w-full">
                  {/* Force the OpenUI Lang Card to claim the full
                      chat column. MessageContent defaults to
                      `w-fit` (shrink to content), which left
                      cards floating left with empty space on
                      the right and surfaced the "seemingly
                      broken UI" feel — a Card with `width: full`
                      inside a `w-fit` parent collapses to its
                      intrinsic content width. */}
                  <MessageContent className="!w-full !max-w-full">
                    <RendererBoundary fallbackText={split.dsl}>
                      <MemoizedDslRenderer
                        response={split.dsl}
                        isStreaming={isThisStreaming}
                        onContinue={handleRendererContinue}
                      />
                    </RendererBoundary>
                  </MessageContent>
                </Message>
              );
            }

            return (
              <Message key={`${message.id}-${partIndex}`} from={message.role}>
                <MessageContent>
                  {/* Render segments in order — markdown for text,
                      JSON-fenced viz components for embedded
                      visualization payloads. Used for tool-heavy
                      turns and user messages. */}
                  {segments.map((seg, segIndex) => {
                    if (seg.type === 'viz' && seg.vizData) {
                      const VisualizationComponent = visualizationRegistry[seg.vizData.type as keyof typeof visualizationRegistry];
                      return VisualizationComponent ? (
                        <div key={`${message.id}-${partIndex}-seg-${segIndex}`} className="my-4">
                          <VisualizationComponent element={{ props: seg.vizData.props as never }} />
                        </div>
                      ) : null;
                    } else if (seg.type === 'html') {
                      return (
                        <InlineHtmlBlock key={`${message.id}-${partIndex}-seg-${segIndex}`} html={seg.content} />
                      );
                    } else {
                      // Text segment may contain trailing OpenUI Lang DSL
                      // (e.g. SuggestedQueries / FollowUpBlock after an HTML
                      // block). Detect and route through Renderer so the
                      // chip keeps its styling — without this branch the
                      // DSL renders as raw text "root = Card(...)" below
                      // the rich HTML.
                      const innerSplit =
                        message.role === 'assistant' && !dashboardToolUsed
                          ? splitProseAndDSL(seg.content)
                          : { prose: seg.content, dsl: null as string | null };
                      if (innerSplit.dsl) {
                        const isThisStreaming =
                          status === 'streaming' &&
                          partIndex === message.parts.length - 1 &&
                          isLastMessage;
                        return (
                          <div key={`${message.id}-${partIndex}-seg-${segIndex}`}>
                            {innerSplit.prose && innerSplit.prose.trim() && (
                              <MessageResponse>{innerSplit.prose}</MessageResponse>
                            )}
                            <RendererBoundary fallbackText={innerSplit.dsl}>
                              <MemoizedDslRenderer
                                response={innerSplit.dsl}
                                isStreaming={isThisStreaming}
                                onContinue={handleRendererContinue}
                              />
                            </RendererBoundary>
                          </div>
                        );
                      }
                      return (
                        <MessageResponse key={`${message.id}-${partIndex}-seg-${segIndex}`}>
                          {seg.content}
                        </MessageResponse>
                      );
                    }
                  })}
                </MessageContent>

              </Message>
            );
          }

          case 'file': {
            const filePart = part as { type: 'file'; url: string; mediaType?: string; filename?: string };
            const isImage = filePart.mediaType?.startsWith('image/');
            const isPdf = filePart.mediaType === 'application/pdf';
            const fname = filePart.filename || (isImage ? 'Image' : 'Attachment');

            return (
              <Message key={`${message.id}-${partIndex}`} from={message.role}>
                <MessageContent>
                  {isImage && filePart.url ? (
                    <img
                      src={filePart.url}
                      alt={fname}
                      className="max-w-xs max-h-64 rounded-lg border object-contain"
                      onError={(e) => {
                        // Hide broken thumbnails — prefer no-image
                        // over a torn-page icon. The text fname
                        // chip below makes the attachment legible.
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/50 text-sm">
                      {isPdf ? (
                        <FilePdf weight="duotone" className="size-5 text-red-500 shrink-0" />
                      ) : (
                        <FileIcon weight="duotone" className="size-5 text-muted-foreground shrink-0" />
                      )}
                      <span className="truncate max-w-[200px]">{fname}</span>
                      {filePart.mediaType && (
                        <span className="text-xs text-muted-foreground">{filePart.mediaType}</span>
                      )}
                    </div>
                  )}
                </MessageContent>
              </Message>
            );
          }

          case 'reasoning': {
            // Reasoning that's been folded into the preceding
            // utility-tool group is rendered inside that
            // group's expander — skip the inline chip here so
            // the chat doesn't double-show it.
            if (groupByPartIndex.has(partIndex)) return null;
            return (
              <Reasoning
                key={`${message.id}-${partIndex}`}
                className="w-full"
                isStreaming={
                  status === 'streaming' &&
                  partIndex === message.parts.length - 1 &&
                  isLastMessage
                }
              >
                {/* text-xs: reasoning is machinery — it sits in the same
                    subordinate register as the timeline entries, one size
                    below the narrative text. */}
                <ReasoningTrigger className="text-xs" />
                <ReasoningContent>{part.text}</ReasoningContent>
              </Reasoning>
            );
          }

          default:
            // Check if this is a tool invocation (type is `tool-${toolName}`)
            if (part.type.startsWith('tool-')) {
              // Completed utility tool → collapse into the group. Render group wrapper
              // once (at the first member's position), skip subsequent members.
              const groupInfo = groupByPartIndex.get(partIndex);
              if (groupInfo) {
                if (!groupInfo.isFirst) return null;
                // Build interleaved steps in temporal order
                // — tools and reasoning mixed exactly as the
                // model emitted them. CompletedToolsGroup
                // renders one expandable row per step so
                // each thought block is independently
                // drillable instead of batched at the bottom.
                const interleavedSteps = groupInfo.members.map((m) => {
                  if (m.kind === 'tool') {
                    return {
                      kind: 'tool' as const,
                      key: `${message.id}-${m.partIndex}`,
                      toolName: m.toolName,
                      input: m.input,
                      output: m.output,
                      errored: m.errored,
                      // Work tools (sandbox runs, file reads…) carry their
                      // ORIGINAL standalone card into the expansion, so
                      // opening the timeline entry reveals the exact
                      // renderer (terminal output, artifact expand button)
                      // the chat previously showed inline.
                      card: m.hasCard
                        ? renderToolCard(message.parts[m.partIndex], m.partIndex)
                        : undefined,
                    };
                  }
                  if (m.kind === 'note') {
                    // A short narration folded in from between two merged
                    // groups (see the merge pass above) — rendered as a
                    // quiet aside, not a "Thinking" block.
                    return {
                      kind: 'note' as const,
                      key: `${message.id}-note-${m.partIndex}`,
                      text: m.text,
                    };
                  }
                  return {
                    kind: 'reasoning' as const,
                    key: `${message.id}-r-${m.partIndex}`,
                    text: m.text,
                  };
                });
                const toolsForLegacy = interleavedSteps
                  .filter((s): s is Extract<typeof s, { kind: 'tool' }> => s.kind === 'tool')
                  .map(({ kind: _kind, ...rest }) => rest);
                return (
                  <div key={`${message.id}-cot-${partIndex}`} className="my-2">
                    <CompletedToolsGroup
                      tools={toolsForLegacy}
                      steps={interleavedSteps}
                    />
                  </div>
                );
              }

              return renderToolCard(part, partIndex);
            }
            return null;
        }
      })}

      {/* Message actions — Copy + thumbs up/down on every assistant turn;
          Retry stays last-message-only (regenerating an earlier turn would
          orphan everything after it). Appear on hover, matching the
          existing row-hover convention (group/turn set on the outer wrapper
          above). `focus-within:opacity-100` mirrors the hover reveal for
          keyboard/touch users — hover-only reveal was invisible to them
          (CHAT_UI_DESIGN_REVIEW.md §4 item 9). */}
      {!readOnly && message.role === 'assistant' && (
        <div className="mt-1 flex items-center gap-0.5 opacity-0 transition-opacity group-hover/turn:opacity-100 focus-within:opacity-100">
          <button
            onClick={() => {
              const allText = message.parts
                .filter((p) => p.type === 'text')
                .map((p) => (p as { type: 'text'; text: string }).text)
                .join('\n\n');
              navigator.clipboard.writeText(allText);
            }}
            aria-label="Copy"
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <Copy weight="bold" className="size-3" />
            Copy
          </button>
          <button
            onClick={() => submitFeedback('up')}
            disabled={feedbackSending}
            aria-pressed={feedbackRating === 'up'}
            aria-label="Good response"
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
              feedbackRating === 'up' ? 'text-emerald-400' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ThumbsUp weight={feedbackRating === 'up' ? 'fill' : 'bold'} className="size-3" />
          </button>
          <button
            onClick={() => submitFeedback('down')}
            disabled={feedbackSending}
            aria-pressed={feedbackRating === 'down'}
            aria-label="Bad response"
            className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
              feedbackRating === 'down' ? 'text-red-400' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ThumbsDown weight={feedbackRating === 'down' ? 'fill' : 'bold'} className="size-3" />
          </button>
          {isLastMessage && (
            <button
              onClick={() => {
                if (hasTurnErrorMarker(message)) {
                  handleRetryFromFailure();
                } else {
                  regenerate();
                }
              }}
              aria-label="Retry"
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <ArrowsClockwise weight="bold" className="size-3" />
              Retry
            </button>
          )}
        </div>
      )}

      {/* User-message actions — Copy + "Branch from here". Branching forks a NEW
          chat that copies everything BEFORE this message; this message text is
          pre-filled into the branch composer as an editable draft (not sent).
          It is Retry's big sibling — Retry regenerates in place, Branch forks so
          the original is never touched. Only offered once the chat has a saved
          session (nothing to fork before then). Same hover/focus reveal as the
          assistant action row. */}
      {!readOnly && message.role === 'user' && handleBranch && currentSessionId && (
        <div className="mt-1 flex items-center justify-end gap-0.5 opacity-0 transition-opacity group-hover/turn:opacity-100 focus-within:opacity-100">
          <button
            onClick={() => {
              const allText = message.parts
                .filter((p) => p.type === 'text')
                .map((p) => (p as { type: 'text'; text: string }).text)
                .join('\n\n');
              navigator.clipboard.writeText(allText);
            }}
            aria-label="Copy"
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <Copy weight="bold" className="size-3" />
            Copy
          </button>
          <button
            onClick={() => {
              const draftText = message.parts
                .filter((p) => p.type === 'text')
                .map((p) => (p as { type: 'text'; text: string }).text)
                .join('\n\n');
              handleBranch(message.id, draftText);
            }}
            aria-label="Branch from here"
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          >
            <GitBranch weight="bold" className="size-3" />
            Branch
          </button>
        </div>
      )}

      {/* Follow-up suggestion chips — server-generated (never canned text),
          shown once the turn has finished. Clicking one fills the composer
          and sends immediately (same click-to-send pattern as
          AskUserQuestionCard / ConnectionRequestCard below). */}
      {showFollowUps && (
        <div className="mt-3 flex flex-wrap gap-2">
          {(followUpSuggestions as string[]).slice(0, 3).map((suggestion, i) => (
            <button
              key={`${message.id}-followup-${i}`}
              type="button"
              onClick={() => handleSubmit({ text: suggestion, files: [] })}
              className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-violet-500/25 bg-violet-500/[0.06] px-3 py-1.5 text-xs text-violet-200 transition-colors hover:border-violet-500/45 hover:bg-violet-500/[0.12] hover:text-violet-100"
            >
              <ArrowBendDownRight weight="bold" className="size-3 shrink-0 opacity-70" />
              <span className="truncate">{suggestion}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

// Consumer-side icon lookup so lib/video-category-presets.ts stays
// dependency-free (it's also imported by the media studio).
const VIDEO_CATEGORY_ICONS: Record<VideoCategoryId, typeof Sparkle> = {
  'motion-graphic': Sparkle,
  'explainer': ChalkboardTeacher,
  'product-promo': RocketLaunch,
  'site-tour': Globe,
  'music-video': MusicNotes,
  'ai-clip': FilmSlate,
};

// Composer modes ("build-type quick actions") — the packaging of existing
// capabilities. Pure-data config + server steer live in @/lib/composer-modes
// (shared with apps/server turn assembly); the icon is the only client-only
// bit, keyed by id so TS forces exhaustive coverage of every mode.
const COMPOSER_MODE_ICONS: Record<ComposerModeId, typeof Sparkle> = {
  video: VideoCamera,
  slides: PresentationChart,
  app: SquaresFour,
  research: MagnifyingGlass,
  viz: Graph,
  write: FileText,
};

// A row of build-mode chips. Selecting one flips the composer into that mode
// (persistent for the session until cleared): the placeholder swaps to the
// mode's hint and the outgoing turn carries `mode` in the request body, which
// the server turns into a per-mode system steer. Clicking the active chip (its
// trailing ✕) clears the mode. `variant="cards"` is a touch larger for the
// empty-state hub; `variant="chips"` is the compact in-chat row.
const ComposerModeBar = memo(function ComposerModeBar({
  selectedMode,
  onSelectMode,
  variant = 'chips',
  className,
}: {
  selectedMode: ComposerModeId | null;
  onSelectMode: (id: ComposerModeId) => void;
  variant?: 'chips' | 'cards';
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label="Build mode"
      className={`flex flex-wrap items-center gap-1.5 ${className ?? ''}`}
    >
      {COMPOSER_MODES.map((mode) => {
        const Icon = COMPOSER_MODE_ICONS[mode.id];
        const active = selectedMode === mode.id;
        return (
          <button
            key={mode.id}
            type="button"
            aria-pressed={active}
            aria-label={active ? `Clear ${mode.label} mode` : `${mode.label} mode`}
            title={`${mode.label} — ${mode.capability}`}
            onClick={() => onSelectMode(mode.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border font-semibold transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${
              variant === 'cards' ? 'px-3.5 py-2 text-[13px]' : 'px-3 py-1.5 text-xs'
            } ${
              active
                ? 'border-violet-500/50 bg-violet-500/[0.14] text-violet-100'
                : 'border-white/[0.06] bg-white/[0.02] text-[#c9c9c9] hover:border-violet-500/30 hover:bg-violet-500/[0.07] hover:text-violet-100'
            }`}
          >
            <Icon weight={active ? 'fill' : 'regular'} className="size-3.5 shrink-0" />
            <span className="whitespace-nowrap">{mode.label}</span>
            {active && <X weight="bold" className="ml-0.5 size-2.5 shrink-0 opacity-80" aria-hidden />}
          </button>
        );
      })}
    </div>
  );
});

const HomeModeGrid = memo(function HomeModeGrid({
  isPro,
  onLockedMode,
  onPrefill,
}: {
  isPro: boolean;
  onLockedMode: () => void;
  onPrefill: (prefill: string) => void;
}) {
  // The "AI Videos" tile opens a category picker (Video Plan Phase 3) instead
  // of prefilling directly — each category prefills its own prompt template.
  const [showVideoCategories, setShowVideoCategories] = useState(false);

  return (
    <div>
      <div className="mt-6 grid grid-cols-4 gap-x-3 gap-y-5 sm:grid-cols-7 sm:gap-x-6">
        {omniModeOptions.map((option) => {
          const Icon = option.icon;
          const isLocked = !!option.proOnly && !isPro;
          const isVideosTile = option.id === 'videos';
          const isOpen = isVideosTile && showVideoCategories;
          return (
            <button
              key={option.id}
              type="button"
              aria-expanded={isVideosTile ? isOpen : undefined}
              onClick={() => {
                if (isLocked) {
                  onLockedMode();
                  return;
                }
                if (isVideosTile) {
                  setShowVideoCategories((open) => !open);
                  return;
                }
                setShowVideoCategories(false);
                onPrefill(option.prefill);
              }}
              className="relative grid min-w-0 justify-items-center gap-2 rounded-xl text-center outline-none transition-transform hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-violet-400"
            >
              {option.badge && (
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-md bg-[#3985bd]/85 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-[#eaf8ff]">
                  {option.badge}
                </span>
              )}
              <span
                className={`grid size-[46px] place-items-center rounded-full border bg-[#171717] shadow-[inset_0_1px_0_rgba(255,255,255,0.03)] transition-colors ${
                  isOpen ? 'border-violet-400/60 ring-2 ring-violet-500/25' : 'border-white/[0.04]'
                }`}
              >
                {isLocked ? (
                  <LockSimple weight="bold" className="size-5 text-muted-foreground" />
                ) : (
                  <Icon weight="regular" className={`size-[21px] ${option.colorClass}`} />
                )}
              </span>
              <span className="whitespace-nowrap text-[12px] font-bold text-[#efefef]">
                {option.label}
              </span>
              {isLocked && (
                <span className="sr-only">Pro only</span>
              )}
            </button>
          );
        })}
      </div>

      <AnimatePresence initial={false}>
        {showVideoCategories && (
          <motion.div
            key="video-categories"
            initial={{ opacity: 0, height: 0, y: -6 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -6 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mt-4 rounded-2xl border border-border/80 bg-card/40 p-3 text-left backdrop-blur-md">
              <div className="mb-2 flex items-center justify-between px-1">
                <span className="text-[10px] font-medium uppercase tracking-[1.5px] text-violet-400">
                  Pick a video style
                </span>
                <button
                  type="button"
                  onClick={() => setShowVideoCategories(false)}
                  aria-label="Close video styles"
                  className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X weight="bold" className="size-3.5" />
                </button>
              </div>
              <div className="grid gap-1.5 sm:grid-cols-2">
                {VIDEO_CATEGORY_PRESETS.map((preset) => {
                  const PresetIcon = VIDEO_CATEGORY_ICONS[preset.id];
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => {
                        onPrefill(preset.chatPrefill);
                        setShowVideoCategories(false);
                      }}
                      className="group flex items-start gap-2.5 rounded-xl border border-transparent px-2.5 py-2 text-left outline-none transition-colors hover:border-violet-500/30 hover:bg-violet-500/[0.06] focus-visible:ring-2 focus-visible:ring-violet-400"
                    >
                      <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border border-white/[0.05] bg-[#171717] transition-colors group-hover:border-violet-500/30">
                        <PresetIcon weight="duotone" className="size-4 text-violet-300" />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] font-semibold leading-tight text-[#efefef]">
                          {preset.label}
                        </span>
                        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                          {preset.tagline}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

// Higgsfield-style model pill living INSIDE the composer footer (next to
// attach/search). Opens the model dialog: Auto routing, provider-grouped
// options (OpenRouter / DigitalOcean / NVIDIA), and free-text custom ids —
// type any vendor/model slug in the search box and pick "Use custom model".
const PROVIDER_META: Record<string, { label: string; badge: string; badgeClass: string }> = {
  openrouter: { label: 'OpenRouter', badge: 'OR', badgeClass: 'bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  digitalocean: { label: 'DigitalOcean', badge: 'DO', badgeClass: 'bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  nvidia: { label: 'NVIDIA NIM', badge: 'NIM', badgeClass: 'bg-lime-500/15 text-lime-600 dark:text-lime-400' },
};

const CUSTOM_MODEL_ID_PATTERN = /^[\w.@:-]+\/[\w.@:-]+$/;

const ComposerModelPill = memo(function ComposerModelPill({
  models,
  selectedModel,
  selectedOption,
  defaultModel,
  onSelect,
}: {
  models: ModelOption[];
  selectedModel: string | null;
  selectedOption?: ModelOption;
  defaultModel: string | null;
  onSelect: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const pick = useCallback((id: string | null) => {
    onSelect(id);
    setOpen(false);
    setQuery('');
  }, [onSelect]);

  const customCandidate = query.trim();
  const showCustomCandidate =
    CUSTOM_MODEL_ID_PATTERN.test(customCandidate) &&
    !models.some((model) => model.id === customCandidate);
  const activeCustomModel =
    selectedModel && !selectedOption ? selectedModel : null;
  const pillLabel = selectedOption?.name
    ?? (activeCustomModel ? activeCustomModel.split('/').pop() : null)
    ?? 'Auto';

  return (
    <ModelSelector open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}>
      <ModelSelectorTrigger asChild>
        <button
          type="button"
          aria-label="Choose chat model"
          className="flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Cpu weight="bold" className="size-4 shrink-0" />
          <span className="max-w-[130px] truncate">{pillLabel}</span>
          <CaretDown weight="bold" className="size-3 shrink-0 opacity-60" />
        </button>
      </ModelSelectorTrigger>
      <ModelSelectorContent className="sm:max-w-xl" title="Choose chat model">
        <ModelSelectorInput
          placeholder="Search models, or type a custom vendor/model id..."
          value={query}
          onValueChange={setQuery}
        />
        <ModelSelectorList>
          <ModelSelectorEmpty>No models found.</ModelSelectorEmpty>
          <ModelSelectorGroup heading="Routing">
            <ModelSelectorItem
              value={`Auto default ${defaultModel ?? ''}`}
              onSelect={() => pick(null)}
              className="gap-3"
            >
              <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                AUTO
              </span>
              <div className="min-w-0 flex-1">
                <ModelSelectorName className="text-sm font-medium">Auto routing</ModelSelectorName>
                <div className="truncate text-xs text-muted-foreground">
                  Uses the platform default{defaultModel ? ` · ${defaultModel}` : ''} and file-aware routing
                </div>
              </div>
              {!selectedModel && <Check weight="bold" className="ml-auto size-4 shrink-0 text-primary" />}
            </ModelSelectorItem>
          </ModelSelectorGroup>
          {(['openrouter', 'digitalocean', 'nvidia'] as const).map((providerKey) => {
            const meta = PROVIDER_META[providerKey];
            const group = models.filter((model) => (model.provider ?? 'openrouter') === providerKey);
            if (group.length === 0) return null;
            return (
              <ModelSelectorGroup key={providerKey} heading={meta.label}>
                {group.map((model) => (
                  <ModelSelectorItem
                    key={model.id}
                    value={`${model.name} ${model.id} ${meta.label}`}
                    onSelect={() => pick(model.id)}
                    className="gap-3"
                  >
                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${meta.badgeClass}`}>
                      {meta.badge}
                    </span>
                    <div className="min-w-0 flex-1">
                      <ModelSelectorName className="text-sm font-medium">{model.name}</ModelSelectorName>
                      <div className="truncate text-xs text-muted-foreground">
                        {model.id}
                        {model.id === defaultModel ? ' · default' : ''}
                        {model.context > 0 ? ` · ${(model.context / 1000).toFixed(0)}k ctx` : ''}
                        {model.creditEstimate ? ` · ${model.creditEstimate.credits} credits est.` : ''}
                      </div>
                    </div>
                    {model.id === selectedModel && <Check weight="bold" className="ml-auto size-4 shrink-0 text-primary" />}
                  </ModelSelectorItem>
                ))}
              </ModelSelectorGroup>
            );
          })}
          {(showCustomCandidate || activeCustomModel) && (
            <ModelSelectorGroup heading="Custom">
              {activeCustomModel && (
                <ModelSelectorItem
                  value={`${activeCustomModel} custom active`}
                  onSelect={() => pick(activeCustomModel)}
                  className="gap-3"
                >
                  <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    CUSTOM
                  </span>
                  <div className="min-w-0 flex-1">
                    <ModelSelectorName className="text-sm font-medium">{activeCustomModel}</ModelSelectorName>
                    <div className="truncate text-xs text-muted-foreground">Custom model id · routed via OpenRouter</div>
                  </div>
                  <Check weight="bold" className="ml-auto size-4 shrink-0 text-primary" />
                </ModelSelectorItem>
              )}
              {showCustomCandidate && (
                <ModelSelectorItem
                  value={`${customCandidate} custom use`}
                  onSelect={() => pick(customCandidate)}
                  className="gap-3"
                >
                  <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    CUSTOM
                  </span>
                  <div className="min-w-0 flex-1">
                    <ModelSelectorName className="text-sm font-medium">Use “{customCandidate}”</ModelSelectorName>
                    <div className="truncate text-xs text-muted-foreground">Any OpenRouter model id works — billed at cost</div>
                  </div>
                </ModelSelectorItem>
              )}
            </ModelSelectorGroup>
          )}
        </ModelSelectorList>
      </ModelSelectorContent>
    </ModelSelector>
  );
});

type HomeComposerHandle = {
  prefill: (prefill: string) => void;
};

/**
 * Upload-on-attach adapter for the chat composer: pushes each attached file
 * straight to S3 (video/audio → presigned multipart with auto-transcription;
 * images → form post) and returns the canonical URL. This is what keeps
 * base64/data URLs out of the messages DB — a video attachment that used to
 * reach the model as an empty `data:` URL now carries a real S3 URL.
 */
async function uploadChatAttachment(
  file: File,
): Promise<{ url: string; mediaType?: string; filename?: string }> {
  const res = await uploadFileToMediaLibrary(file);
  return { url: res.url, mediaType: res.mimeType, filename: res.filename };
}

function describeAttachError(err: { code: string; message: string }): string {
  if (err.code === 'accept') {
    return `That file type isn't supported. You can attach ${CHAT_UPLOAD_SUPPORTED_LABEL}.`;
  }
  return err.message;
}

/**
 * Composer mic button — click to record (MediaRecorder, webm/opus), click
 * again to stop + transcribe; the transcript is APPENDED into the composer
 * text, never auto-sent. Shared by both HomeComposer and ChatComposer so the
 * recording state machine + capability check live in exactly one place.
 *
 * Graceful degrade: renders nothing when the browser can't record audio at
 * all (`!supported`, e.g. no MediaRecorder / getUserMedia) OR the server has
 * no transcription backend configured (`!available`, from the
 * GET /api/transcribe-audio capability check) — no dead button that always
 * fails. Permission-denied and transcription-failure both toast; recording
 * state is a solid red pulsing dot, unmissable next to the Search toggle.
 */
function ComposerMicButton({ onAppend }: { onAppend: (text: string) => void }) {
  const voice = useVoiceRecorder({
    onTranscript: onAppend,
    onError: (message) => toast.error(message),
  });

  if (!voice.supported || !voice.available) return null;

  const isRecording = voice.status === 'recording';
  const isTranscribing = voice.status === 'transcribing';

  return (
    <PromptInputButton
      variant={isRecording ? 'default' : 'ghost'}
      onClick={voice.toggle}
      disabled={isTranscribing}
      aria-pressed={isRecording}
      aria-label={isRecording ? 'Stop recording' : 'Record a voice message'}
      className={isRecording ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse' : undefined}
    >
      <Microphone weight={isRecording ? 'fill' : 'regular'} size={16} />
      {isRecording ? <span>Recording…</span> : isTranscribing ? <span>Transcribing…</span> : null}
    </PromptInputButton>
  );
}

const HomeComposer = memo(forwardRef<HomeComposerHandle, {
  initialInput: string;
  status: ChatStatus;
  webSearch: boolean;
  modelPill?: React.ReactNode;
  selectedMode: ComposerModeId | null;
  onSelectMode: (id: ComposerModeId) => void;
  onSubmit: (message: PromptInputMessage) => void;
  onStop: () => void;
  onToggleWebSearch: () => void;
}>(function HomeComposer({
  initialInput,
  status,
  webSearch,
  modelPill,
  selectedMode,
  onSelectMode,
  onSubmit,
  onStop,
  onToggleWebSearch,
}, ref) {
  const [homeInput, setHomeInput] = useState(initialInput);
  const [attachError, setAttachError] = useState<string | null>(null);
  const typewriterPrompts = useMemo(() => pickSuggestions(5), []);
  const rotatingPlaceholder = useRotatingPlaceholder(typewriterPrompts, !homeInput);
  const modeHint = getComposerMode(selectedMode)?.hint;
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBusy = status === 'streaming' || status === 'submitted';

  const prefill = useCallback((nextInput: string) => {
    setHomeInput(nextInput);
    requestAnimationFrame(() => {
      const ta = document.querySelector<HTMLTextAreaElement>('textarea[data-home-composer="true"]');
      if (ta) {
        ta.focus();
        const end = nextInput.length;
        ta.setSelectionRange(end, end);
      }
    });
  }, []);

  useImperativeHandle(ref, () => ({ prefill }), [prefill]);

  useEffect(() => {
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (homeInput) {
        localStorage.setItem('galexia-draft-input', homeInput);
      } else {
        localStorage.removeItem('galexia-draft-input');
      }
    }, 250);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [homeInput]);

  const handleHomeSubmit = useCallback((message: PromptInputMessage) => {
    setAttachError(null);
    onSubmit(message);
    setHomeInput('');
  }, [onSubmit]);

  return (
    <div className="w-full">
      <ComposerModeBar
        variant="cards"
        selectedMode={selectedMode}
        onSelectMode={onSelectMode}
        className={selectedMode === 'slides' ? 'mb-2.5 justify-center' : 'mb-3 justify-center'}
      />
      <AnimatePresence initial={false}>
        {selectedMode === 'slides' && (
          <motion.div
            key="slide-templates"
            initial={{ opacity: 0, height: 0, y: -6 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -6 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="mb-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3 text-left">
              <div className="mb-2 px-0.5 text-[10px] font-medium uppercase tracking-[1.5px] text-violet-400">
                Start from a template
              </div>
              <SlideTemplateGallery onPick={(draft) => prefill(draft)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    <PromptInput
      onSubmit={handleHomeSubmit}
      className="w-full rounded-[22px] border border-[#2a2a2a] bg-[#151515]/90 p-0 text-left shadow-[0_24px_80px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.035)]"
      globalDrop
      multiple
      accept={CHAT_UPLOAD_ACCEPT}
      uploadAttachment={uploadChatAttachment}
      onError={(err) => setAttachError(describeAttachError(err))}
    >
      <PromptInputHeader>
        <PromptInputAttachments>
          {(attachment) => <PromptInputAttachment data={attachment} />}
        </PromptInputAttachments>
      </PromptInputHeader>
      {attachError && (
        <div className="px-3 pt-2 text-xs text-destructive" role="alert">
          {attachError}
        </div>
      )}
      <PromptInputBody className="px-3 pt-3">
        <PromptInputTextarea
          data-home-composer="true"
          value={homeInput}
          onChange={(e) => setHomeInput(e.target.value)}
          placeholder={modeHint ?? (rotatingPlaceholder || 'A cinematic intro for a tech product...')}
          className="min-h-[44px] text-[14px] font-semibold placeholder:text-[#858585]"
        />
      </PromptInputBody>
      <PromptInputFooter className="px-3 pb-2">
        <PromptInputTools>
          <PromptInputActionMenu>
            <PromptInputActionMenuTrigger />
            <PromptInputActionMenuContent>
              <PromptInputActionAddAttachments />
            </PromptInputActionMenuContent>
          </PromptInputActionMenu>

          <PromptInputButton
            variant={webSearch ? 'default' : 'ghost'}
            onClick={onToggleWebSearch}
          >
            <Globe weight={webSearch ? 'fill' : 'regular'} size={16} />
            <span>Search</span>
          </PromptInputButton>

          <ComposerMicButton onAppend={(text) => setHomeInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))} />

          {modelPill}
        </PromptInputTools>

        <PromptInputSubmit
          disabled={!homeInput.trim() && !isBusy}
          status={status}
          type={isBusy ? 'button' : 'submit'}
          onClick={
            isBusy
              ? (e) => { e.preventDefault(); onStop(); }
              : undefined
          }
        />
      </PromptInputFooter>
    </PromptInput>
    </div>
  );
}));

const ChatComposer = memo(function ChatComposer({
  initialInput,
  status,
  webSearch,
  modelPill,
  selectedMode,
  onSelectMode,
  onSubmit,
  onStop,
  onToggleWebSearch,
}: {
  initialInput: string;
  status: ChatStatus;
  webSearch: boolean;
  modelPill?: React.ReactNode;
  selectedMode: ComposerModeId | null;
  onSelectMode: (id: ComposerModeId) => void;
  onSubmit: (message: PromptInputMessage) => void;
  onStop: () => void;
  onToggleWebSearch: () => void;
}) {
  const [draft, setDraft] = useState(initialInput);
  const modeHint = getComposerMode(selectedMode)?.hint;
  const [attachError, setAttachError] = useState<string | null>(null);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isBusy = status === 'streaming' || status === 'submitted';

  useEffect(() => {
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (draft) {
        localStorage.setItem('galexia-draft-input', draft);
      } else {
        localStorage.removeItem('galexia-draft-input');
      }
    }, 250);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [draft]);

  const handleSubmitDraft = useCallback((message: PromptInputMessage) => {
    setAttachError(null);
    onSubmit(message);
    setDraft('');
  }, [onSubmit]);

  return (
    <div className="mt-4">
      <div className="mb-2 flex flex-wrap items-center gap-1.5 px-1">
        <ComposerModeBar
          variant="chips"
          selectedMode={selectedMode}
          onSelectMode={onSelectMode}
        />
        {selectedMode === 'slides' && (
          <SlideTemplatesButton onPick={(draft) => setDraft(draft)} />
        )}
      </div>
    <PromptInput
      onSubmit={handleSubmitDraft}
      globalDrop
      multiple
      accept={CHAT_UPLOAD_ACCEPT}
      uploadAttachment={uploadChatAttachment}
      onError={(err) => setAttachError(describeAttachError(err))}
    >
      <PromptInputHeader>
        <PromptInputAttachments>
          {(attachment) => <PromptInputAttachment data={attachment} />}
        </PromptInputAttachments>
      </PromptInputHeader>
      {attachError && (
        <div className="px-1 pt-2 text-xs text-destructive" role="alert">
          {attachError}
        </div>
      )}
      <PromptInputBody>
        <PromptInputTextarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={modeHint ?? 'Ask Galexia to research, build, analyze, or create...'}
        />
      </PromptInputBody>
      <PromptInputFooter>
        <PromptInputTools>
          <PromptInputActionMenu>
            <PromptInputActionMenuTrigger />
            <PromptInputActionMenuContent>
              <PromptInputActionAddAttachments />
            </PromptInputActionMenuContent>
          </PromptInputActionMenu>

          <PromptInputButton
            variant={webSearch ? 'default' : 'ghost'}
            onClick={onToggleWebSearch}
          >
            <Globe weight={webSearch ? 'fill' : 'regular'} size={16} />
            <span>Search</span>
          </PromptInputButton>

          <ComposerMicButton onAppend={(text) => setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))} />

          {modelPill}
        </PromptInputTools>

        <PromptInputSubmit
          disabled={!draft.trim() && !isBusy}
          status={status}
          type={isBusy ? 'button' : 'submit'}
          onClick={
            isBusy
              ? (e) => { e.preventDefault(); onStop(); }
              : undefined
          }
        />
      </PromptInputFooter>
    </PromptInput>
    </div>
  );
});

const HomePromptHub = memo(function HomePromptHub({
  initialInput,
  isPro,
  status,
  webSearch,
  modelPill,
  selectedMode,
  onSelectMode,
  onSubmit,
  onStop,
  onToggleWebSearch,
  onLockedMode,
}: {
  initialInput: string;
  isPro: boolean;
  status: ChatStatus;
  webSearch: boolean;
  modelPill?: React.ReactNode;
  selectedMode: ComposerModeId | null;
  onSelectMode: (id: ComposerModeId) => void;
  onSubmit: (message: PromptInputMessage) => void;
  onStop: () => void;
  onToggleWebSearch: () => void;
  onLockedMode: () => void;
}) {
  const composerRef = useRef<HomeComposerHandle>(null);

  const applyHomePrefill = useCallback((prefill: string) => {
    composerRef.current?.prefill(prefill);
  }, []);

  return (
    <div className="relative flex min-h-[calc(100svh-150px)] flex-col items-center justify-center overflow-hidden px-4 py-6">
      {/* Reference-style top aurora, translated into the existing app shell. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 overflow-hidden"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(circle at 18% 6%, rgba(122,42,203,0.35), transparent 28%), radial-gradient(circle at 46% 2%, rgba(185,42,92,0.30), transparent 25%), radial-gradient(circle at 86% 9%, rgba(0,179,197,0.25), transparent 32%), linear-gradient(to bottom, rgba(17,17,17,0.9) 0%, transparent 52%)',
          }}
        />
        <div
          className="absolute inset-0 blur-xl"
          style={{
            background:
              'radial-gradient(ellipse at top left, rgba(103,50,180,0.28), transparent 36%), radial-gradient(ellipse at top right, rgba(0,178,204,0.22), transparent 37%), radial-gradient(ellipse at top center, rgba(147,37,83,0.20), transparent 35%)',
          }}
        />
      </div>
      <div className="fixed bottom-2 left-1/2 hidden h-1.5 w-9 -translate-x-1/2 rounded-full border border-white/30 bg-white/[0.04] sm:block" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-[640px] text-center"
      >
        <div className="mb-2 inline-flex items-center justify-center gap-2.5">
          <GalexiaLogo size={28} />
          <h1 className="text-[26px] font-extrabold leading-none tracking-normal text-[#d7d7d7] sm:text-[28px]">
            Galexia<span className="bg-gradient-to-r from-[#a767ff] via-[#ff5db8] to-[#ffb020] bg-clip-text text-transparent">OmniAgent</span>
          </h1>
        </div>

        <p className="mx-auto mb-5 max-w-[560px] text-[14px] font-medium leading-relaxed tracking-normal text-[#c3c3c3] sm:text-[15px]">
          One agent. Infinite creativity. Generate images, videos, music, documents, deep research, and more. Just ask!
        </p>

        <HomeComposer
          ref={composerRef}
          initialInput={initialInput}
          status={status}
          webSearch={webSearch}
          modelPill={modelPill}
          selectedMode={selectedMode}
          onSelectMode={onSelectMode}
          onSubmit={onSubmit}
          onStop={onStop}
          onToggleWebSearch={onToggleWebSearch}
        />

        <HomeModeGrid
          isPro={isPro}
          onLockedMode={onLockedMode}
          onPrefill={applyHomePrefill}
        />
      </motion.div>

      <div className="pointer-events-none fixed inset-x-0 bottom-7 z-20 flex justify-center px-4 md:left-14">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.34 }}
          className="pointer-events-auto w-full max-w-[360px]"
        >
          <HomeFeatureCarousel onPrefill={applyHomePrefill} />
        </motion.div>
      </div>
    </div>
  );
});

export function GenUIChat({ sessionId: initialSessionId = null, readOnly = false, adminView = false, initialMessages, evalConfig }: ChatViewProps) {
  const isEvalMode = !!evalConfig;
  const [input, setInput] = useState('');
  // Restore draft after hydration — avoids SSR/client mismatch that can swallow early keystrokes
  useEffect(() => {
    if (isEvalMode) return;
    const draft = localStorage.getItem('galexia-draft-input');
    if (draft) setInput(draft);
  }, [isEvalMode]);
  // Restore draft when the user signs in — covers the "typed → submit → auth modal → sign in → input went blank" flow
  const prevSignedInRef = useRef<boolean | undefined>(undefined);
  const [webSearch, setWebSearch] = useState(false);
  // Composer build mode ("video" / "slides" / …). Persists for the session
  // until the user clears it (clicks the active chip). Sent as `mode` in the
  // chat request body → server injects a per-mode system steer (turn.ts).
  const [composerMode, setComposerMode] = useState<ComposerModeId | null>(null);
  const composerModeRef = useRef<ComposerModeId | null>(null);
  composerModeRef.current = composerMode;
  const handleSelectComposerMode = useCallback((id: ComposerModeId) => {
    setComposerMode((cur) => (cur === id ? null : id));
  }, []);
  const [currentSessionId, setLocalSessionId] = useState<string | null>(initialSessionId);
  // Steering texts queued while the agent runs — shown as chips until the
  // turn finishes (the persisted transcript then contains them properly).
  const [steeredNotices, setSteeredNotices] = useState<string[]>([]);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const authDialogRef = useRef<HTMLDivElement>(null);
  // Standard dialog a11y plumbing for the auth-prompt modal — focus trap,
  // Esc to dismiss, body-scroll lock, focus restore on close.
  useModalA11y(showAuthPrompt && !readOnly && !isEvalMode, {
    dialogRef: authDialogRef,
    onDismiss: () => setShowAuthPrompt(false),
  });
  const [usageLimitError, setUsageLimitError] = useState<{
    message: string;
    remaining: number;
    limit: number;
    plan: string;
  } | null>(null);
  // Esc-to-dismiss for the usage-limit toast. Toasts don't get the full
  // modal-a11y treatment (no focus trap, no body-scroll lock — they're not
  // modal) but Esc convenience matches other dismissible UI in the app.
  useEffect(() => {
    if (!usageLimitError || readOnly || isEvalMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUsageLimitError(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [usageLimitError, readOnly, isEvalMode]);
  const [chatError, setChatError] = useState<ChatErrorInfo | null>(null);
  const [sessionLoading, setSessionLoading] = useState(!!initialSessionId && !isEvalMode);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [codeMode, setCodeMode] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [availableChatModels, setAvailableChatModels] = useState<ModelOption[]>([]);
  const [defaultChatModel, setDefaultChatModel] = useState<string | null>(null);
  const [selectedChatModel, setSelectedChatModel] = useState<string | null>(null);
  const [artifactKey, setArtifactKey] = useState<string | null>(null);
  const [isArtifactOpen, setIsArtifactOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  // History panel (session/turn history — revert, bookmarks, undo last edit).
  const [historyOpen, setHistoryOpen] = useState(false);
  // Bookmarked turn ordinals (sessions.bookmarked_turns) — loaded with the
  // session, toggled optimistically, persisted via POST /:id/bookmarks.
  const [bookmarkedTurns, setBookmarkedTurns] = useState<number[]>([]);
  // Right-side dashboard panel state. Auto-set when a create_dashboard /
  // update_dashboard / show_dashboard tool result lands; user can close
  // via the panel's X / Esc and re-open via a "view" chip on the tool row.
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(null);
  // In-flight dashboard preview. When the model is mid-stream of a
  // create_dashboard / update_dashboard tool call, this carries the
  // partial spec so DashboardPanel can render it live (instead of waiting
  // for the row to be saved server-side). Cleared on output-available.
  const [streamingDashboard, setStreamingDashboard] = useState<StreamingDashboard | null>(null);
  // Change-key of the last committed streamingDashboard — see the watcher
  // effect; avoids a per-token setState during dashboard spec streaming.
  const streamingDashboardKeyRef = useRef<string | null>(null);
  // Desktop nav collapse is shared via context (client-layout owns the
  // state). When an artifact panel opens we auto-collapse the chat-history
  // rail to free up horizontal space; user can manually expand again.
  const { setDesktopCollapsed } = useMobileSidebar();
  // Track whether we've already auto-collapsed for the current
  // open-an-artifact transition so we don't fight the user if they
  // re-expand the rail while a dashboard / viz panel is showing.
  const lastArtifactStateRef = useRef<{ hadDashboard: boolean; hadArtifact: boolean }>({
    hadDashboard: false,
    hadArtifact: false,
  });
  // Bumped every time a dashboard tool result lands for the active dashboard.
  // DashboardPanel uses [dashboardId, refreshKey] as its fetch deps, so even
  // when update_dashboard returns the SAME id (so setActiveDashboardId is a
  // React no-op via Object.is), the panel still re-fetches and re-renders
  // with the new spec.
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  // Track which tool-call ids we've already auto-opened so we don't yank
  // the panel back open after the user manually closed it.
  const dashboardOpenedToolCallsRef = useRef<Set<string>>(new Set());
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submitInFlightRef = useRef(false);
  // Tracks the PREVIOUS status so the follow-up-suggestions effect below can
  // detect the streaming/submitted -> ready transition (a completed turn),
  // not just "status is currently ready" (true on every idle render too).
  const prevChatStatusRef = useRef<ChatStatus>('ready');
  const autoSendRef = useRef(false);
  const currentSessionIdRef = useRef<string | null>(initialSessionId);
  // Read via ref inside the transport — useChat holds onto the transport it
  // was created with, so a closure over state would send a stale (usually
  // null) model override forever. Same pattern as currentSessionIdRef.
  const selectedChatModelRef = useRef<string | null>(null);
  const resumedRunIdRef = useRef<string | null>(null);
  // Direct-mode mount resume is done manually (guarded), not via useChat's
  // `resume:` option — see the effect below the workflow-resume one.
  const directResumeSessionRef = useRef<string | null>(null);
  const pendingClientTtftRef = useRef<PendingClientTtftMeasurement | null>(null);
  const lastLatestArtifactKeyRef = useRef<string | null>(null);
  const lastWorkspaceOpenRequestRef = useRef<string | null>(null);
  const trackedTelemetryKeysRef = useRef<Set<string>>(new Set());
  const artifactOpenSourceRef = useRef<string>('unknown');
  const artifactCloseReasonRef = useRef<string>('unknown');
  const previousArtifactStateRef = useRef<{ isOpen: boolean; artifact: ArtifactCandidate | null }>({
    isOpen: false,
    artifact: null,
  });
  const router = useRouter();
  const searchParams = useSearchParams();

  // Prefill from ?q= once on mount when starting a fresh chat (e.g. landed
  // here from the not-found page's suggestions). Doesn't auto-submit — user
  // reviews and hits send. Skipped in eval mode and existing sessions.
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (initialSessionId || isEvalMode) return;
    const q = searchParams?.get('q');
    if (q && q.trim().length > 0) {
      prefillAppliedRef.current = true;
      setInput(q);
      // Clean the URL so back-button + refresh don't keep re-prefilling.
      try {
        router.replace('/', { scroll: false });
      } catch {
        /* router.replace can throw mid-render in edge cases — non-fatal */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isSignedIn } = useAuth();
  const { user } = useUser();

  // Restore the pre-auth draft when the user transitions from signed-out → signed-in.
  // Flow: signed-out user types → clicks submit → auth modal opens (PromptInput's form.reset blanks DOM)
  // → user signs in → this effect restores their text so they can submit again.
  useEffect(() => {
    if (isEvalMode) return;
    if (prevSignedInRef.current === false && isSignedIn === true) {
      const draft = localStorage.getItem('galexia-draft-input');
      if (draft) setInput(draft);
    }
    prevSignedInRef.current = isSignedIn;
  }, [isSignedIn, isEvalMode]);

  const isPro = user?.publicMetadata?.plan === 'pro' || user?.publicMetadata?.plan === 'team';
  const isAdmin = user?.publicMetadata?.role === 'admin';
  const { setCurrentSessionId, refreshSessions, loadSession, pollSessionMetadata, pollLatestAssistantMetadata, sessions } = useSession();
  // New-session side effects (sidebar refresh + metadata polling) fire from
  // the transport's response handler when the server-created session id is
  // adopted from the x-session-id header. Kept in a ref so the transport
  // memo doesn't rebuild on these functions' identity.
  const sessionAdoptionFnsRef = useRef({ refreshSessions, pollSessionMetadata });
  useEffect(() => {
    sessionAdoptionFnsRef.current = { refreshSessions, pollSessionMetadata };
  }, [refreshSessions, pollSessionMetadata]);
  // Derive the active session's title from the sessions list so the browser
  // tab announces which chat is open. Falls through to the layout default
  // ("Galexia - Your AI workspace for real work.") when no session is active.
  const activeSessionTitle = useMemo(() => {
    if (!currentSessionId) return undefined;
    return sessions.find((s) => s.id === currentSessionId)?.title;
  }, [currentSessionId, sessions]);
  useDocumentTitle(activeSessionTitle);
  const planTier = user?.publicMetadata?.plan === 'team'
    ? 'team'
    : user?.publicMetadata?.plan === 'pro'
      ? 'pro'
      : 'free';
  const selectedChatModelOption = useMemo(
    () => getModelOptionById(availableChatModels, selectedChatModel),
    [availableChatModels, selectedChatModel],
  );

  // Composer-footer model pill (Higgsfield-style) — shared by the home hub
  // and the in-chat composer.
  const composerModelPill = useMemo(() => {
    if (readOnly || isEvalMode || availableChatModels.length === 0) return null;
    return (
      <ComposerModelPill
        models={availableChatModels}
        selectedModel={selectedChatModel}
        selectedOption={selectedChatModelOption}
        defaultModel={defaultChatModel}
        onSelect={setSelectedChatModel}
      />
    );
  }, [readOnly, isEvalMode, availableChatModels, selectedChatModel, selectedChatModelOption, defaultChatModel]);

  const trackChatTelemetry = useCallback((eventName: string, properties: Record<string, string | number | boolean | null | undefined> = {}) => {
    captureProductTelemetry(eventName, {
      session_id: currentSessionId ?? undefined,
      plan_tier: planTier,
      is_desktop: isDesktop,
      ...properties,
    });
  }, [currentSessionId, isDesktop, planTier]);

  // Persist draft input to localStorage (debounced) — skip in eval mode
  useEffect(() => {
    if (isEvalMode) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      if (input) {
        localStorage.setItem('galexia-draft-input', input);
      } else {
        localStorage.removeItem('galexia-draft-input');
      }
    }, 300);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [input, isEvalMode]);

  useEffect(() => {
    if (isEvalMode) {
      return;
    }

    let cancelled = false;

    // Read the persisted pick synchronously, BEFORE the fetch: the writer
    // effect below runs on mount with the initial null state and overwrites
    // the key with 'auto', so reading it after the await always lost the
    // user's saved model.
    const storedModel =
      typeof window !== 'undefined'
        ? window.localStorage.getItem(CHAT_MODEL_STORAGE_KEY)
        : null;

    async function loadChatModels() {
      try {
        const response = await fetch('/api/models');
        if (!response.ok) {
          throw new Error('Failed to load chat models');
        }

        const data = await response.json() as {
          availableModels?: ModelOption[];
          defaultModel?: string | null;
        };

        if (cancelled) {
          return;
        }

        const models = Array.isArray(data.availableModels) ? data.availableModels : [];
        const defaultModel = typeof data.defaultModel === 'string' ? data.defaultModel : null;
        const nextModel =
          storedModel === 'auto' || !storedModel
            ? null
            : getModelOptionById(models, storedModel)?.id
              // Custom vendor/model slugs survive reloads even though they
              // aren't in models.available (routed via OpenRouter).
              ?? (storedModel.includes('/') ? storedModel : null);

        setAvailableChatModels(models);
        setDefaultChatModel(defaultModel);
        setSelectedChatModel(nextModel);
      } catch (error) {
        console.error('Failed to load chat models:', error);
      }
    }

    void loadChatModels();

    return () => {
      cancelled = true;
    };
  }, [isEvalMode]);

  useEffect(() => {
    if (isEvalMode || typeof window === 'undefined') {
      return;
    }

    selectedChatModelRef.current = selectedChatModel;
    if (selectedChatModel) {
      window.localStorage.setItem(CHAT_MODEL_STORAGE_KEY, selectedChatModel);
    } else {
      window.localStorage.setItem(CHAT_MODEL_STORAGE_KEY, 'auto');
    }
  }, [isEvalMode, selectedChatModel]);

  // Switch to canvas mode
  const handleSwitchToCanvas = useCallback(() => {
    if (currentSessionId) {
      router.push(`/canvas/${currentSessionId}`);
    } else {
      router.push('/canvas');
    }
  }, [currentSessionId, router]);

  const transport = useMemo(() => {
    if (isEvalMode) {
      return new DefaultChatTransport({
        api: evalConfig?.api ?? '/api/chat',
        ...(evalConfig?.body ? { body: evalConfig.body } : {}),
      });
    }

    const prepareSendMessagesRequest: PrepareSendMessagesRequest<UIMessage> = ({ body, messages, ...rest }) => ({
      ...rest,
      body: {
        messages,
        ...(body ?? {}),
        chatModelOverride: selectedChatModelRef.current ?? undefined,
        sessionId: currentSessionIdRef.current ?? undefined,
      },
    });
    const handleChatResponse = (response: Response) => {
      const responseSessionId = response.headers.get('x-session-id') ?? currentSessionIdRef.current;

      if (responseSessionId && responseSessionId !== currentSessionIdRef.current) {
        const isNewSession = currentSessionIdRef.current == null;
        currentSessionIdRef.current = responseSessionId;
        setLocalSessionId(responseSessionId);

        if (isNewSession) {
          // The server created this session during the chat POST (the client
          // no longer pre-creates it with a blocking round trip). Mirror the
          // URL — route-aware so /labs/genui refreshes re-mount the lab —
          // and kick off sidebar refresh + title/metadata polling.
          const prefix = window.location.pathname.startsWith('/labs/genui') ? '/labs/genui' : '/chat';
          replaceChatUrl(`${prefix}/${responseSessionId}`, responseSessionId);
          setTimeout(() => {
            sessionAdoptionFnsRef.current.refreshSessions();
            sessionAdoptionFnsRef.current.pollSessionMetadata(responseSessionId);
          }, 500);
        }
      }

      return responseSessionId;
    };

    if (!USE_WORKFLOW_CHAT_TRANSPORT) {
      return new DefaultChatTransport({
        api: '/api/chat',
        fetch: async (input, init) => {
          const response = await fetch(input, init);
          handleChatResponse(response);
          return response;
        },
        prepareSendMessagesRequest,
        // Re-attach to a live stream after refresh/network drop. The resume
        // endpoint answers 204 instantly when nothing is streaming, so the
        // on-mount reconnect is a single cheap request.
        prepareReconnectToStreamRequest: ({ api: _api, ...rest }) => {
          const sessionId = currentSessionIdRef.current;
          if (!sessionId) {
            throw new Error('No session id available for stream resume');
          }
          return {
            ...rest,
            api: `/api/chat/${encodeURIComponent(sessionId)}/resume`,
          };
        },
      });
    }

    return new WorkflowChatTransport({
      api: '/api/chat',
      onChatSendMessage: (response) => {
        const responseSessionId = handleChatResponse(response);
        const workflowRunId = response.headers.get('x-workflow-run-id');

        if (workflowRunId && responseSessionId) {
          setStoredWorkflowRunId(responseSessionId, workflowRunId);
          resumedRunIdRef.current = workflowRunId;
        }
      },
      onChatEnd: () => {
        if (currentSessionIdRef.current) {
          clearStoredWorkflowRunId(currentSessionIdRef.current);
        }
      },
      prepareSendMessagesRequest,
      prepareReconnectToStreamRequest: ({ api: _api, ...rest }) => {
        const sessionId = currentSessionIdRef.current;
        if (sessionId == null) {
          throw new Error('No active session ID found for workflow stream reconnection');
        }

        const runId = getStoredWorkflowRunId(sessionId);
        if (runId == null) {
          throw new Error('No active workflow run ID found for this session');
        }

        return {
          ...rest,
          api: `/api/chat/${encodeURIComponent(runId)}/stream`,
          headers: {
            ...(rest.headers ?? {}),
            'x-session-id': sessionId,
          },
        };
      },
    });
  }, [evalConfig?.api, evalConfig?.body, isEvalMode]);

  const { messages, sendMessage, status, regenerate, resumeStream, setMessages, stop } = useChat({
    ...(evalConfig?.chatId ? { id: evalConfig.chatId } : {}),
    // Direct-mode mount resume is NOT done via this option anymore: when the
    // resume endpoint answers 204 (nothing streaming — the common case),
    // @ai-sdk/react's internal resumeStream throws an UNCATCHABLE TypeError
    // ("reading 'state'") into the console on every session open. The manual
    // effect below (mirroring the workflow-resume effect) performs the same
    // one cheap request with a .catch, so a 204 is silent and a live stream
    // still resumes.
    resume: false,
    // Batch chunk -> React updates to one per 50ms (imperceptible; still
    // ~20fps text). Without this, when a render takes longer than the chunk
    // interval, the SDK's store mutates during render 50+ times in a row and
    // React throws "Maximum update depth exceeded", killing the generation.
    experimental_throttle: 50,
    transport,
    onError: async (error) => {
      // On-mount stream resume against a session with nothing to resume (or
      // one this user doesn't own — e.g. opening someone else's link) fails
      // through DefaultChatTransport.reconnectToStream. That's not a
      // generation failure — there is no generation. Swallow it instead of
      // rendering a scary "Generation failed" alert on page load.
      if (
        error.message?.includes('Session not found or access denied') ||
        (error.message?.includes('/resume') && error.stack?.includes('reconnectToStream')) ||
        error.stack?.includes('reconnectToStream')
      ) {
        console.warn('[Chat] Ignored stream-resume failure (nothing to resume):', error.message);
        return;
      }

      // Check if it's a usage limit error (429)
      if (error.message?.includes('usage_limit') || (error as { status?: number }).status === 429) {
        try {
          const match = error.message?.match(/\{.*\}/);
          if (match) {
            const errorData = JSON.parse(match[0]);
            if (errorData.error === 'usage_limit') {
              setUsageLimitError({
                message: errorData.message,
                remaining: errorData.remaining,
                limit: errorData.limit,
                plan: errorData.plan,
              });
              return;
            }
          }
        } catch {
          // If parsing fails, show generic limit message
        }
        setUsageLimitError({
          message: "You don't have enough Galexia credits for this request.",
          remaining: 0,
          limit: 5,
          plan: 'free',
        });
        return;
      }

      setChatError(formatChatError(error));
      console.error('Chat error:', error);
    },
  });

  const hasRecoverableTurnFailure = useMemo(
    () => messages.some((message) => message.role === 'assistant' && hasTurnErrorMarker(message)),
    [messages],
  );

  const handleRetryFromFailure = useCallback(() => {
    setChatError(null);
    setUsageLimitError(null);

    const cleanedMessages = stripTurnErrorMarkersFromMessages(messages);
    setMessages(cleanedMessages);
    const clientRequestId = createClientRequestId();
    pendingClientTtftRef.current = {
      clientRequestId,
      startedAt: performance.now(),
      messageCountAtSubmit: cleanedMessages.length,
      sessionId: currentSessionIdRef.current,
      chatModel: selectedChatModel,
      route: window.location.pathname,
      executionMode: USE_WORKFLOW_CHAT_TRANSPORT ? 'workflow' : 'direct',
    };

    sendMessage(
      { text: appendSystemTime(RETRY_FROM_FAILURE_TEXT) },
      {
        body: {
          sessionId: currentSessionIdRef.current ?? undefined,
          retryFromFailure: true,
          clientRequestId,
        },
      },
    );
  }, [messages, selectedChatModel, sendMessage, setMessages]);

  // Conversation branching — fork a new chat from an earlier user message. The
  // server COPIES the transcript prefix into a new session (never mutating this
  // one); we pre-fill the branch-point message into the new composer via the
  // existing localStorage draft channel (restored by the branch's mount effect),
  // refresh the sidebar so the branch appears, then navigate. A real navigation
  // remounts GenUIChat on the new session (chat-shell keys on the URL).
  const handleBranch = useCallback(async (messageId: string, draftText: string) => {
    if (!currentSessionId) return;
    try {
      const res = await fetch(`/api/sessions/${currentSessionId}/branch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ atMessageId: messageId }),
      });
      if (!res.ok) {
        console.error('[branch] request failed:', res.status);
        return;
      }
      const { newSessionId } = await res.json();
      if (!newSessionId) return;
      try {
        localStorage.setItem('galexia-draft-input', draftText ?? '');
      } catch {
        // localStorage may be unavailable (private mode) — the branch still
        // opens, just without the pre-filled draft.
      }
      await sessionAdoptionFnsRef.current.refreshSessions();
      router.push(`/chat/${newSessionId}`);
    } catch (err) {
      console.error('[branch] error:', err);
    }
  }, [currentSessionId, router]);

  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
    resumedRunIdRef.current = null;
  }, [currentSessionId]);

  useEffect(() => {
    if (status !== 'submitted' && status !== 'streaming') {
      submitInFlightRef.current = false;
      // Turn ended — steered messages are now part of the persisted turn.
      setSteeredNotices((prev) => (prev.length > 0 ? [] : prev));
    }
  }, [status]);

  useEffect(() => {
    const pending = pendingClientTtftRef.current;
    if (!pending) return;

    const newMessages = messages.slice(pending.messageCountAtSubmit);
    const hasFirstOutput = newMessages.some(hasVisibleAssistantOutput);
    if (!hasFirstOutput) return;

    pendingClientTtftRef.current = null;
    sendClientPerformanceMetric({
      clientRequestId: pending.clientRequestId,
      sessionId: currentSessionIdRef.current ?? pending.sessionId,
      metricType: 'chat_client_ttft',
      valueMs: Math.round(performance.now() - pending.startedAt),
      route: pending.route,
      chatModel: pending.chatModel,
      executionMode: pending.executionMode,
      metadata: {
        status,
        messageCountAtSubmit: pending.messageCountAtSubmit,
        messageCountAtFirstOutput: messages.length,
      },
    });
  }, [messages, status]);

  // Post-turn follow-up suggestion chips (feature: chat-view follow-ups).
  // The server generates them AFTER the durable turn's finalize step
  // completes (fire-and-forget, non-blocking — see chat/finalize.ts), so
  // they are never present the instant `status` flips back to 'ready'. Fire
  // a short bounded poll on exactly the streaming/submitted -> ready
  // transition (a completed turn), merge the suggestions into the finished
  // assistant message when they land. If the poll times out, a reload of
  // the session still surfaces them (persisted on the message row).
  useEffect(() => {
    const prevStatus = prevChatStatusRef.current;
    prevChatStatusRef.current = status;
    if (isEvalMode || readOnly) return;
    if (status !== 'ready' || (prevStatus !== 'streaming' && prevStatus !== 'submitted')) return;

    const sessionId = currentSessionIdRef.current;
    if (!sessionId) return;
    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.role !== 'assistant') return;
    if ((lastMessage.metadata as Record<string, unknown> | undefined)?.followUpSuggestions) return;

    const targetMessageId = lastMessage.id;
    pollLatestAssistantMetadata(sessionId, (metadata) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === targetMessageId
            ? { ...m, metadata: { ...(m.metadata as Record<string, unknown> | undefined), ...metadata } }
            : m,
        ),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Update global session context when local changes — skip in eval mode
  useEffect(() => {
    if (isEvalMode) return;
    if (currentSessionId) {
      setCurrentSessionId(currentSessionId);
    }
  }, [currentSessionId, setCurrentSessionId, isEvalMode]);

  // Load messages when we have a session ID (either initial or after creation) — skip in eval mode
  useEffect(() => {
    if (isEvalMode) return;
    if (initialSessionId) {
      setLocalSessionId(initialSessionId);

      // If pre-loaded messages were provided (e.g. shared chat page), use them directly
      if (initialMessages) {
        setMessages(initialMessages.map(msg => ({
          id: msg.id,
          role: msg.role as 'user' | 'assistant' | 'system',
          parts: msg.parts as never[],
          metadata: msg.metadata as Record<string, unknown> | undefined,
        })));
        setSessionLoading(false);
        return;
      }

      setSessionLoading(true);
      setSessionError(null);
      const loadSessionMessages = async () => {
        try {
          if (adminView) {
            // Admin view: fetch with admin query param to bypass ownership check
            const res = await fetch(`/api/sessions/${initialSessionId}?admin=true`);
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              setSessionError(data.error || `Failed to load session (${res.status})`);
              setSessionLoading(false);
              return;
            }
            const data = await res.json();
            setMessages(data.messages.map((msg: { id: string; role: string; parts: unknown[]; metadata?: unknown }) => ({
              id: msg.id,
              role: msg.role,
              parts: msg.parts,
              metadata: msg.metadata,
            })));
          } else {
            const loadedSession = await loadSession(initialSessionId);
            if (loadedSession) {
              setMessages(loadedSession.messages);
              setBookmarkedTurns(loadedSession.bookmarkedTurns);
              if (loadedSession.activeWorkflowRunId) {
                setStoredWorkflowRunId(initialSessionId, loadedSession.activeWorkflowRunId);
              } else {
                clearStoredWorkflowRunId(initialSessionId);
              }
            } else {
              setSessionError('Session not found or access denied');
            }
          }
        } catch (err) {
          console.error('Failed to load session:', err);
          setSessionError(err instanceof Error ? err.message : 'Failed to load session');
        } finally {
          setSessionLoading(false);
        }
      };
      loadSessionMessages();
    }
  }, [initialSessionId, loadSession, setMessages, adminView, isEvalMode]);

  useEffect(() => {
    if (isEvalMode || sessionLoading || currentSessionId == null || status !== 'ready') return;

    const runId = getStoredWorkflowRunId(currentSessionId);
    if (runId == null || resumedRunIdRef.current === runId) return;

    resumedRunIdRef.current = runId;
    void resumeStream().catch((error) => {
      console.error('Failed to resume workflow stream:', error);
      clearStoredWorkflowRunId(currentSessionId);
      resumedRunIdRef.current = null;
    });
  }, [currentSessionId, isEvalMode, resumeStream, sessionLoading, status]);

  // Direct-mode mount resume (replaces useChat's `resume:` option — see the
  // comment there). One request per opened session: 204 = nothing streaming
  // (the SDK's 204 handling throws internally; swallowed here), 200 = a live
  // stream gets re-attached. Runs after the snapshot load so the resumed
  // stream lands on the loaded transcript, never before it.
  useEffect(() => {
    if (USE_WORKFLOW_CHAT_TRANSPORT || isEvalMode || sessionLoading || status !== 'ready') return;
    if (!initialSessionId || directResumeSessionRef.current === initialSessionId) return;

    directResumeSessionRef.current = initialSessionId;
    void resumeStream().catch(() => {
      // 204/no-active-stream is the overwhelmingly common case — silence it.
      // A genuinely broken resume shows up as the turn's own error surface.
    });
  }, [initialSessionId, isEvalMode, resumeStream, sessionLoading, status]);

  // Auto-send prompt in eval mode (once, on mount)
  useEffect(() => {
    if (!isEvalMode || !evalConfig?.autoSend || autoSendRef.current) return;
    autoSendRef.current = true;
    sendMessage({ text: evalConfig.autoSend });
  }, [isEvalMode, evalConfig?.autoSend, sendMessage]);

  const handleSubmit = useCallback(async (message: PromptInputMessage) => {
    // Mid-run steering: submitting while the agent is generating queues the
    // text server-side (Redis); the direct-mode loop injects it between
    // steps — right after the currently running tool completes.
    if (status === 'submitted' || status === 'streaming') {
      const steerText = message.text?.trim();
      const steerSessionId = currentSessionIdRef.current;
      if (!steerText || !steerSessionId || USE_WORKFLOW_CHAT_TRANSPORT || isEvalMode) return;
      setSteeredNotices((prev) => [...prev, steerText]);
      setInput('');
      fetch('/api/chat/steer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: steerSessionId, text: steerText }),
      }).then((res) => {
        if (!res.ok) {
          setSteeredNotices((prev) => prev.filter((notice) => notice !== steerText));
          console.error('[Chat] Steering failed:', res.status);
        }
      }).catch((err) => {
        setSteeredNotices((prev) => prev.filter((notice) => notice !== steerText));
        console.error('[Chat] Steering failed:', err);
      });
      return;
    }
    if (submitInFlightRef.current) {
      return;
    }

    const hasText = Boolean(message.text?.trim());
    const hasAttachments = Boolean(message.files?.length);

    if (!(hasText || hasAttachments)) return;
    submitInFlightRef.current = true;

    // Clear any previous errors
    setChatError(null);
    setUsageLimitError(null);

    // Eval mode: just send the message directly, no session/auth logic
    if (isEvalMode) {
      sendMessage({ text: appendSystemTime(message.text || ''), files: message.files });
      setInput('');
      return;
    }

    // Check if user is signed in before allowing submission. In development
    // mode the server-side `LOCAL_DEV_BYPASS_USER_ID` env stands in for a
    // real Clerk session — letting the API run while the client is signed
    // out. Skip the client-side auth modal under that same flag so the lab
    // is testable end-to-end without going through Clerk.
    const devBypass = process.env.NODE_ENV === 'development';
    if (!isSignedIn && !devBypass) {
      // Persist the draft immediately (the 300ms debounce might not have fired yet)
      // so we can restore it after the user signs in.
      if (message.text && message.text.trim()) {
        localStorage.setItem('galexia-draft-input', message.text);
      }
      setShowAuthPrompt(true);
      submitInFlightRef.current = false;
      return;
    }

    // Zero round trips for new chats: the client MINTS the session UUID and
    // sends it with the first message — /api/chat creates the session row
    // under that id (idempotent on retry; ownership enforced server-side).
    // URL, sidebar refresh, and metadata polling all start immediately
    // instead of waiting for response headers.
    let sessionIdToUse = currentSessionId;
    if (!sessionIdToUse) {
      sessionIdToUse = crypto.randomUUID();
      currentSessionIdRef.current = sessionIdToUse;
      setLocalSessionId(sessionIdToUse);
      const prefix = window.location.pathname.startsWith('/labs/genui') ? '/labs/genui' : '/chat';
      replaceChatUrl(`${prefix}/${sessionIdToUse}`, sessionIdToUse);
      const mintedId = sessionIdToUse;
      setTimeout(() => {
        sessionAdoptionFnsRef.current.refreshSessions();
        sessionAdoptionFnsRef.current.pollSessionMetadata(mintedId);
      }, 1200);
    }

    const clientRequestId = createClientRequestId();
    pendingClientTtftRef.current = {
      clientRequestId,
      startedAt: performance.now(),
      messageCountAtSubmit: messages.length,
      sessionId: sessionIdToUse,
      chatModel: selectedChatModel,
      route: window.location.pathname,
      executionMode: USE_WORKFLOW_CHAT_TRANSPORT ? 'workflow' : 'direct',
    };

    sendMessage(
      {
        text: appendSystemTime(message.text || 'Describe this image'),
        files: message.files,
      },
      {
        body: {
          sessionId: sessionIdToUse,
          webSearchRequested: webSearch,
          clientRequestId,
          // Composer build mode steer (read via ref so selecting a mode does
          // not invalidate this stable callback). undefined when no mode.
          ...(composerModeRef.current ? { mode: composerModeRef.current } : {}),
        },
      }
    );
    setInput('');
    setWebSearch(false);
    localStorage.removeItem('galexia-draft-input');

  }, [currentSessionId, messages.length, selectedChatModel, sendMessage, isSignedIn, isEvalMode, webSearch, status]);

  // Auto-send a seeded first message from ?prompt= on a FRESH chat only.
  // Used by surfaces that kick off a guided conversation (e.g. the /agents
  // "create by chatting with Galexia" path). Safety: fresh chat only (no
  // session), runs once, plain-text + length-capped, and the param is stripped
  // from the URL before sending so refresh/back never re-sends.
  const seedSentRef = useRef(false);
  useEffect(() => {
    if (seedSentRef.current) return;
    if (initialSessionId || currentSessionId || isEvalMode) return;
    const raw = searchParams?.get('prompt');
    if (!raw) return;
    const seed = raw.replace(/\s+/g, ' ').trim().slice(0, 2000);
    if (!seed) return;
    seedSentRef.current = true;
    // Strip ?prompt= immediately (synchronous, no navigation churn) so a
    // refresh or back-button never resends. handleSubmit then mints the
    // session URL over this cleaned path.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      /* replaceState can throw in rare embedded contexts — non-fatal */
    }
    void handleSubmit({ text: seed, files: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit]);

  // Stable onAction handler for MemoizedDslRenderer. Depends only on
  // handleSubmit (itself stable during token streaming), so completed DSL
  // turns keep referential equality and skip re-render while a later message
  // streams.
  const handleRendererContinue = useCallback((text: string) => {
    handleSubmit({ text, files: [] });
  }, [handleSubmit]);

  // Hard stop: abort client fetch + cancel server workflow so we don't burn tokens/compute.
  const handleStop = useCallback(() => {
    submitInFlightRef.current = false;
    stop();
    const sid = currentSessionIdRef.current;
    if (sid) {
      fetch('/api/chat/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: sid }),
      }).catch((err) => console.error('Cancel failed:', err));
    }
  }, [stop]);

  const handleHomeWebSearchToggle = useCallback(() => {
    setWebSearch((value) => !value);
  }, []);

  const handleLockedHomeMode = useCallback(() => {
    router.push('/pricing');
  }, [router]);

  // Retry a failed visualization by sending the error back to the LLM
  const handleRetryVizError = useCallback((info: { toolName: string; error: string; code: string }) => {
    const errorReport = [
      `<clientside_error_report>`,
      `Tool: ${info.toolName}`,
      `Error: ${info.error}`,
      `Code that failed:`,
      info.code,
      `</clientside_error_report>`,
    ].join('\n');

    handleSubmit({ text: errorReport, files: [] });
  }, [handleSubmit]);

  // Auto-system-message bus → chat stream. See chat-view.tsx for the why.
  const sentSystemEventsRef = useRef<Set<string>>(new Set());
  const pendingSystemEventsRef = useRef<AutoSystemMessageEvent[]>([]);
  useEffect(() => {
    return onAutoSystemMessage((event) => {
      if (sentSystemEventsRef.current.has(event.eventId)) return;
      if (status === 'streaming' || status === 'submitted') {
        pendingSystemEventsRef.current.push(event);
        return;
      }
      sentSystemEventsRef.current.add(event.eventId);
      sendMessage({ text: formatSystemMessage(event) });
    });
  }, [sendMessage, status]);
  useEffect(() => {
    if (status !== 'ready') return;
    while (pendingSystemEventsRef.current.length > 0) {
      const event = pendingSystemEventsRef.current.shift()!;
      if (sentSystemEventsRef.current.has(event.eventId)) continue;
      sentSystemEventsRef.current.add(event.eventId);
      sendMessage({ text: formatSystemMessage(event) });
    }
  }, [sendMessage, status]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const updateDesktopState = () => setIsDesktop(mediaQuery.matches);

    updateDesktopState();
    mediaQuery.addEventListener('change', updateDesktopState);

    return () => mediaQuery.removeEventListener('change', updateDesktopState);
  }, []);

  const artifactCandidates = useMemo<ArtifactCandidate[]>(() => {
    const candidates: ArtifactCandidate[] = [];

    for (const message of messages) {
      for (let partIndex = 0; partIndex < message.parts.length; partIndex += 1) {
        const part = message.parts[partIndex];
        if (!part.type.startsWith('tool-')) continue;

        const toolPart = part as {
          type: string;
          input?: Record<string, unknown>;
          output?: { success?: boolean; visualizationId?: string } | undefined;
          visualizationId?: string;
        };

        const toolName = toolPart.type.replace('tool-', '');
        const input = toolPart.input;
        const output = toolPart.output;

        if (!output?.success || !input) continue;

        const delegated = resolveDelegatedToolDisplay(toolName, input, output);
        const effectiveInput = delegated.input ?? input;
        const effectiveOutput = delegated.output as { success?: boolean; visualizationId?: string } | undefined;
        const presentation = delegated.presentation ?? resolveVisualizationPresentationMode(delegated.toolName, effectiveInput);
        if (presentation === 'inline' && !supportsArtifactPanel(delegated.toolName, effectiveInput, effectiveOutput)) continue;

        candidates.push({
          key: `${message.id}-${partIndex}`,
          toolName: delegated.toolName,
          input: effectiveInput,
          output: effectiveOutput ?? output,
          visualizationId: effectiveOutput?.visualizationId || output.visualizationId || toolPart.visualizationId,
          presentation,
        });
      }
    }

    return candidates;
  }, [messages]);

  const activeArtifact = useMemo(
    () => artifactCandidates.find(candidate => candidate.key === artifactKey) ?? null,
    [artifactCandidates, artifactKey],
  );
  const workspaceOpenRequestKey = useMemo(() => getWorkspaceOpenRequestKey(messages), [messages]);

  // Auto-open the right-side dashboard panel when a dashboard tool result
  // lands. Watches every assistant tool part for create_dashboard /
  // update_dashboard / show_dashboard with `output.id`. The
  // `dashboardOpenedToolCallsRef` set keeps us from re-yanking the panel
  // back open once the user has manually closed it for that tool call.
  useEffect(() => {
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      for (const p of m.parts) {
        if (!p.type.startsWith('tool-')) continue;
        const tp = p as { state?: string; output?: unknown; toolCallId?: string; type: string };
        if (tp.state !== 'output-available') continue;
        if (!tp.toolCallId || dashboardOpenedToolCallsRef.current.has(tp.toolCallId)) continue;
        const name = tp.type.replace('tool-', '');
        if (!DASHBOARD_TOOL_NAMES.has(name)) continue;
        const out = tp.output as { id?: string; opened?: boolean } | undefined;
        if (out?.id && out.opened !== false) {
          dashboardOpenedToolCallsRef.current.add(tp.toolCallId);
          setActiveDashboardId(out.id);
          // Force a re-fetch in DashboardPanel — handles update_dashboard
          // where the id stays the same (so setActiveDashboardId is a
          // no-op for React) but the spec on the server has changed.
          setDashboardRefreshKey((k) => k + 1);
        }
      }
    }
  }, [messages]);

  // Watch for editVisualization tool completions and publish to the
  // refresh bus. LiveRemotionRenderer (and any other live consumer)
  // subscribes by visualizationId and re-fetches the saved source so
  // the rendered video / artifact updates in place without a reload.
  // The ref tracks which tool-call ids we've already published so a
  // re-render of this effect doesn't double-bump.
  const editVizPublishedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      for (const p of m.parts) {
        if (p.type !== 'tool-editVisualization') continue;
        const tp = p as {
          state?: string;
          output?: { success?: boolean; visualizationId?: string };
          toolCallId?: string;
        };
        if (tp.state !== 'output-available') continue;
        if (!tp.toolCallId || editVizPublishedRef.current.has(tp.toolCallId)) continue;
        const vizId = tp.output?.visualizationId;
        if (tp.output?.success && vizId) {
          editVizPublishedRef.current.add(tp.toolCallId);
          bumpVisualizationVersion(vizId);
        }
      }
    }
  }, [messages]);

  // Track in-flight dashboard tool calls and surface the partial spec
  // to DashboardPanel for live preview. Walks the most recent assistant
  // message looking for a create_/update_dashboard part still in
  // input-streaming or input-available state with a usable spec string.
  // Cleared as soon as the tool completes (output-available branch
  // upstream takes over via setActiveDashboardId).
  useEffect(() => {
    let inflight: StreamingDashboard | null = null;
    let inflightToolCallId: string | null = null;
    for (const m of messages) {
      if (m.role !== 'assistant') continue;
      for (const p of m.parts) {
        if (!p.type.startsWith('tool-')) continue;
        const tp = p as {
          state?: string;
          input?: { spec?: unknown; name?: unknown; theme?: unknown };
          toolCallId?: string;
          type: string;
        };
        const toolName = tp.type.replace('tool-', '');
        if (toolName !== 'create_dashboard' && toolName !== 'update_dashboard') continue;
        if (tp.state !== 'input-streaming' && tp.state !== 'input-available') continue;
        const spec = typeof tp.input?.spec === 'string' ? tp.input.spec : '';
        // Need at least a partial root assignment for the renderer to
        // attempt parsing. Below ~5 chars and the boundary will trip.
        if (spec.length < 5) continue;
        inflight = {
          spec,
          name: typeof tp.input?.name === 'string' && tp.input.name.length > 0
            ? tp.input.name
            : 'Building…',
          theme: (tp.input?.theme && typeof tp.input.theme === 'object')
            ? (tp.input.theme as StreamingDashboard['theme'])
            : null,
          isStreaming: tp.state === 'input-streaming',
        };
        inflightToolCallId = tp.toolCallId ?? null;
      }
    }
    // Commit only on meaningful change. A per-token setState here is an
    // effect-scheduled update on every stream flush — the pattern that
    // feeds React's nested-update limit ("Maximum update depth exceeded").
    // Live preview still updates every ~64 spec chars / on state changes.
    const nextKey = inflight
      ? `${inflight.name}|${inflight.isStreaming}|${Math.floor(inflight.spec.length / 64)}`
      : null;
    if (nextKey !== streamingDashboardKeyRef.current) {
      streamingDashboardKeyRef.current = nextKey;
      setStreamingDashboard(inflight);
    }
    // When a streaming dashboard appears, also flag the panel as open
    // (no activeDashboardId yet — that lands on output-available).
    if (inflight && inflightToolCallId) {
      // No-op: streamingDashboard alone drives panel rendering when
      // activeDashboardId is null.
    }
  }, [messages]);

  // Auto-collapse the desktop chat-history rail when an artifact panel
  // (dashboard or visualization) opens — give the artifact horizontal
  // breathing room. Only fires on the open-transition; doesn't fight
  // the user if they manually re-expand the rail while it's showing.
  useEffect(() => {
    const hasDashboard = activeDashboardId !== null || streamingDashboard !== null;
    const hasArtifact = isArtifactOpen && activeArtifact !== null;
    const prev = lastArtifactStateRef.current;
    const newlyOpenedDashboard = hasDashboard && !prev.hadDashboard;
    const newlyOpenedArtifact = hasArtifact && !prev.hadArtifact;
    if (newlyOpenedDashboard || newlyOpenedArtifact) {
      setDesktopCollapsed(true);
    }
    lastArtifactStateRef.current = { hadDashboard: hasDashboard, hadArtifact: hasArtifact };
  }, [activeDashboardId, streamingDashboard, isArtifactOpen, activeArtifact, setDesktopCollapsed]);

  useEffect(() => {
    const nextState = deriveArtifactPanelState({
      candidates: artifactCandidates,
      artifactKey,
      isArtifactOpen,
      isDesktop,
      previousLatestKey: lastLatestArtifactKeyRef.current,
    });

    lastLatestArtifactKeyRef.current = nextState.latestArtifactKey;

    if (nextState.autoOpenLatest) {
      artifactOpenSourceRef.current = 'auto-open';
    }

    if (nextState.responsiveClose) {
      artifactCloseReasonRef.current = 'responsive-layout';
    }

    if (nextState.artifactKey !== artifactKey) {
      setArtifactKey(nextState.artifactKey);
    }

    if (nextState.isArtifactOpen !== isArtifactOpen) {
      setIsArtifactOpen(nextState.isArtifactOpen);
    }
  }, [artifactCandidates, artifactKey, isArtifactOpen, isDesktop]);

  useEffect(() => {
    if (!workspaceOpenRequestKey || workspaceOpenRequestKey === lastWorkspaceOpenRequestRef.current) {
      return;
    }

    const latestArtifact = artifactCandidates[artifactCandidates.length - 1];
    if (!latestArtifact) {
      return;
    }

    lastWorkspaceOpenRequestRef.current = workspaceOpenRequestKey;
    artifactOpenSourceRef.current = 'show-workspace-tool';
    setArtifactKey(latestArtifact.key);
    if (isDesktop) {
      setIsArtifactOpen(true);
    }
  }, [artifactCandidates, isDesktop, workspaceOpenRequestKey]);

  const openArtifact = useCallback((candidate: ArtifactCandidate, source: string = 'manual-open') => {
    artifactOpenSourceRef.current = source;
    setArtifactKey(candidate.key);
    setIsArtifactOpen(true);
  }, []);

  const closeArtifact = useCallback((reason: string = 'panel-close') => {
    artifactCloseReasonRef.current = reason;
    setIsArtifactOpen(false);
  }, []);

  // ---- History panel wiring -------------------------------------------------
  // Turns for the history panel — grouped from the live message list (pure).
  const historyTurns = useMemo<HistoryTurn[]>(
    () => groupMessagesIntoTurns(messages as unknown as HistoryMessage[]),
    [messages],
  );
  // Artifact chips keyed to their producing message so the panel can attach
  // them to the right turn. Reuses the already-derived artifactCandidates.
  const historyArtifacts = useMemo<HistoryArtifactChip[]>(
    () =>
      artifactCandidates.map((candidate) => {
        const input = candidate.input as { title?: unknown; name?: unknown; prompt?: unknown } | undefined;
        const rawLabel =
          (typeof input?.title === 'string' && input.title) ||
          (typeof input?.name === 'string' && input.name) ||
          (typeof input?.prompt === 'string' && input.prompt) ||
          getToolLabel(candidate.toolName);
        const label = rawLabel.length > 40 ? `${rawLabel.slice(0, 39)}…` : rawLabel;
        return {
          key: candidate.key,
          // key is `${messageId}-${partIndex}`; messageId (a uuid) may contain
          // hyphens, partIndex never does — split on the LAST hyphen.
          messageId: candidate.key.slice(0, candidate.key.lastIndexOf('-')),
          toolName: candidate.toolName,
          label,
        };
      }),
    [artifactCandidates],
  );

  const persistBookmark = useCallback(
    async (turnIndex: number, next: boolean) => {
      if (!currentSessionId) return;
      // Optimistic — adopt server truth on response, revert on failure.
      const prev = bookmarkedTurns;
      setBookmarkedTurns((cur) =>
        next ? [...new Set([...cur, turnIndex])].sort((a, b) => a - b) : cur.filter((t) => t !== turnIndex),
      );
      try {
        const res = await fetch(`/api/sessions/${currentSessionId}/bookmarks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ turnIndex, bookmarked: next }),
        });
        if (!res.ok) throw new Error(`bookmark failed (${res.status})`);
        const data = await res.json();
        if (Array.isArray(data.bookmarkedTurns)) setBookmarkedTurns(data.bookmarkedTurns);
      } catch (err) {
        console.error('[history] bookmark toggle failed:', err);
        setBookmarkedTurns(prev);
        toast.error('Could not update bookmark');
      }
    },
    [currentSessionId, bookmarkedTurns],
  );

  const handleRevertToTurn = useCallback(
    (turn: HistoryTurn) => {
      setHistoryOpen(false);
      toast('Opening a branch from this turn — your original chat is untouched.');
      handleBranch(turn.userMessageId, turn.fullText);
    },
    [handleBranch],
  );

  const handleUndoLastEdit = useCallback(() => {
    const lastTurn = historyTurns[historyTurns.length - 1];
    if (!lastTurn) return;
    // Explicit confirm (task): "undo last edit" creates a NEW branch, never a
    // destructive delete. Sonner action-toast doubles as the confirm.
    toast('Undo last edit?', {
      description: 'This forks a new branch at your most recent message. The original stays intact.',
      action: {
        label: 'Create branch',
        onClick: () => {
          setHistoryOpen(false);
          handleBranch(lastTurn.userMessageId, lastTurn.fullText);
        },
      },
    });
  }, [historyTurns, handleBranch]);

  const handleOpenArtifactFromHistory = useCallback(
    (key: string) => {
      const candidate = artifactCandidates.find((c) => c.key === key);
      if (!candidate) return;
      setHistoryOpen(false);
      openArtifact(candidate, 'history-panel');
    },
    [artifactCandidates, openArtifact],
  );

  useEffect(() => {
    const currentArtifact = isArtifactOpen ? activeArtifact : null;
    const previous = previousArtifactStateRef.current;

    if (!previous.isOpen && currentArtifact) {
      trackChatTelemetry('artifact_panel_opened', {
        tool_name: currentArtifact.toolName,
        presentation_mode: currentArtifact.presentation,
        visualization_id: currentArtifact.visualizationId,
        open_source: artifactOpenSourceRef.current,
      });
    } else if (previous.isOpen && currentArtifact && previous.artifact?.key !== currentArtifact.key) {
      trackChatTelemetry('artifact_panel_switched', {
        from_tool_name: previous.artifact?.toolName,
        to_tool_name: currentArtifact.toolName,
        from_visualization_id: previous.artifact?.visualizationId,
        to_visualization_id: currentArtifact.visualizationId,
        open_source: artifactOpenSourceRef.current,
      });
    } else if (previous.isOpen && !currentArtifact && previous.artifact) {
      trackChatTelemetry('artifact_panel_closed', {
        tool_name: previous.artifact.toolName,
        presentation_mode: previous.artifact.presentation,
        visualization_id: previous.artifact.visualizationId,
        close_reason: artifactCloseReasonRef.current,
      });
    }

    previousArtifactStateRef.current = {
      isOpen: Boolean(currentArtifact),
      artifact: currentArtifact,
    };
  }, [activeArtifact, isArtifactOpen, trackChatTelemetry]);

  useEffect(() => {
    for (let messageIndex = 0; messageIndex < messages.length; messageIndex += 1) {
      const message = messages[messageIndex];

      for (let partIndex = 0; partIndex < message.parts.length; partIndex += 1) {
        const part = message.parts[partIndex];
        if (!part.type.startsWith('tool-')) continue;

        const telemetryKey = `${message.id}:${partIndex}:${part.type}`;
        if (trackedTelemetryKeysRef.current.has(telemetryKey)) continue;

        const toolPart = part as {
          type: string;
          input?: Record<string, unknown>;
          output?: Record<string, unknown> & { success?: boolean; visualizationId?: string };
          visualizationId?: string;
        };
        const toolName = toolPart.type.replace('tool-', '');
        const output = toolPart.output;
        const input = toolPart.input;

        const delegated = resolveDelegatedToolDisplay(toolName, input, output);
        const effectiveInput = delegated.input ?? input;
        const effectiveOutput = delegated.output as Record<string, unknown> & { success?: boolean; visualizationId?: string } | undefined;
        const effectiveToolName = delegated.toolName;

        if (isVisualizationToolName(effectiveToolName, effectiveInput, effectiveOutput) && effectiveOutput?.success && effectiveInput) {
          trackedTelemetryKeysRef.current.add(telemetryKey);
          trackChatTelemetry('visualization_tool_rendered', {
            tool_name: effectiveToolName,
            presentation_mode: delegated.presentation ?? resolveVisualizationPresentationMode(effectiveToolName, effectiveInput),
            visualization_id: effectiveOutput.visualizationId || output?.visualizationId || toolPart.visualizationId,
            has_artifact_support: supportsArtifactPanel(effectiveToolName, effectiveInput, effectiveOutput),
          });
          continue;
        }

        if (toolName === 'listVisualizationVersions' && output?.success) {
          trackedTelemetryKeysRef.current.add(telemetryKey);
          const versions = Array.isArray(output.versions) ? output.versions : [];
          trackChatTelemetry('visualization_versions_listed', {
            version_count: versions.length,
          });
          continue;
        }

        if ((toolName === 'editVisualization' || toolName === 'revertVisualizationVersion') && output?.success) {
          trackedTelemetryKeysRef.current.add(telemetryKey);
          trackChatTelemetry(
            toolName === 'editVisualization' ? 'visualization_edited' : 'visualization_reverted',
            {
              visualization_id: typeof output.visualizationId === 'string' ? output.visualizationId : undefined,
              version_number: typeof output.versionNumber === 'number' ? output.versionNumber : undefined,
            },
          );
        }
      }
    }
  }, [messages, trackChatTelemetry]);

  const chatLayout = (
    <div className="flex flex-col h-full max-h-full overflow-hidden max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-3 md:py-4">
      {/* Admin View Banner */}
      {readOnly && adminView && !isEvalMode && (
        <div className="mb-3 px-4 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center gap-2">
          <span className="text-amber-600 dark:text-amber-400 text-sm font-medium">Admin View</span>
          <span className="text-xs text-muted-foreground">Read-only — viewing another user&apos;s chat</span>
        </div>
      )}

      {/* Header actions — share, canvas toggle, code mode (admin) — hidden in
          eval mode. The model selector now lives in the composer footer. */}
      {!isEvalMode && ((!readOnly && (currentSessionId && messages.length > 0 || process.env.NEXT_PUBLIC_CANVAS_MODE_ENABLED === 'true')) || isAdmin) && (
        <div className="flex justify-end gap-2 mb-2">
          {!readOnly && currentSessionId && messages.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHistoryOpen(true)}
              className="h-8 gap-1.5"
              aria-label="Open history"
            >
              <ClockCounterClockwise weight="bold" className="size-4" />
              <span className="text-xs">History</span>
            </Button>
          )}
          {!readOnly && currentSessionId && messages.length > 0 && (
            <ChatShareButton sessionId={currentSessionId} />
          )}
          {!readOnly && process.env.NEXT_PUBLIC_CANVAS_MODE_ENABLED === 'true' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSwitchToCanvas}
              className="h-8 gap-1.5"
            >
              <FrameCorners weight="bold" className="size-4" />
              <span className="text-xs">Canvas</span>
            </Button>
          )}
          {isAdmin && messages.length > 0 && (
            <Button
              variant={codeMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCodeMode(prev => !prev)}
              className="h-7 gap-1.5 text-xs"
            >
              {'</>'}
              <span>{codeMode ? 'Chat' : 'Code'}</span>
            </Button>
          )}
        </div>
      )}

      {/* History panel — session turns with revert (branch-based), bookmarks,
          and undo-last-edit. Rendered inside chatLayout (present in every
          layout branch); the Sheet portals so tree position is layout-neutral. */}
      {!isEvalMode && !readOnly && currentSessionId && (
        <HistoryPanel
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          turns={historyTurns}
          bookmarkedTurns={bookmarkedTurns}
          artifacts={historyArtifacts}
          onToggleBookmark={persistBookmark}
          onRevert={handleRevertToTurn}
          onUndoLast={handleUndoLastEdit}
          onOpenArtifact={handleOpenArtifactFromHistory}
        />
      )}

      {/* Code Mode — raw JSON view for admins */}
      {codeMode && isAdmin ? (
        (() => {
          const codeJson = JSON.stringify(
            messages.map(msg => ({
              id: msg.id,
              role: msg.role,
              parts: msg.parts,
              ...(msg.metadata ? { metadata: msg.metadata } : {}),
            })),
            null,
            2
          );
          const handleCopyCode = async () => {
            try {
              await navigator.clipboard.writeText(codeJson);
              setCodeCopied(true);
              window.setTimeout(() => setCodeCopied(false), 2000);
            } catch {
              /* clipboard blocked — silently no-op */
            }
          };
          return (
            <div className="relative flex-1 min-h-0 overflow-auto">
              <div className="sticky top-0 z-10 flex justify-end p-2 pointer-events-none">
                <Button
                  size="sm"
                  onClick={handleCopyCode}
                  aria-label={codeCopied ? 'Copied conversation JSON to clipboard' : 'Copy conversation JSON'}
                  className={`pointer-events-auto shrink-0 gap-1.5 transition-all ${
                    codeCopied
                      ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/20'
                      : 'bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white hover:scale-[1.02]'
                  }`}
                >
                  {codeCopied ? (
                    <>
                      <Check weight="bold" className="size-3.5" aria-hidden="true" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy weight="bold" className="size-3.5" aria-hidden="true" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <pre className="-mt-12 px-4 pb-4 pt-4 text-xs font-mono bg-muted/30 rounded-lg border whitespace-pre-wrap break-words">
                {codeJson}
              </pre>
              <div className="sr-only" role="status" aria-live="polite">
                {codeCopied ? 'Conversation JSON copied to clipboard.' : ''}
              </div>
            </div>
          );
        })()
      ) : (

      /* Chat Area */
      <div className="flex-1 min-h-0">
        <Conversation className="h-full">
          <ConversationContent>
            {sessionLoading ? (
              /* Session loading — branded with the logo pulse + skeleton
                 message rows so the empty space feels purposeful instead
                 of just a spinner. role="status" + aria-busy so screen
                 readers announce the wait without taking visual focus. */
              <div
                className="flex flex-col items-center justify-center min-h-[calc(100vh-160px)] gap-5 px-4"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="galexia-logo-thinking">
                  <GalexiaLogo size={42} />
                </div>
                <p className="text-sm text-muted-foreground/65">Loading conversation…</p>
                <div className="w-full max-w-2xl space-y-3 opacity-50" aria-hidden="true">
                  {[260, 320, 200].map((w, i) => (
                    <div
                      key={i}
                      className="h-3 rounded bg-muted/50"
                      style={{ width: w, animation: `pulse 1.6s ease-in-out ${i * 0.15}s infinite` }}
                    />
                  ))}
                </div>
              </div>
            ) : sessionError ? (
              /* Session error state — branded card matching the rest of the
                 app. role="alert" + aria-live so screen-reader users hear that
                 the conversation failed to load instead of landing on a
                 silent stopped page. */
              <div
                className="flex flex-col items-center justify-center min-h-[calc(100vh-160px)] gap-4 px-4"
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
              >
                <div className="size-12 rounded-2xl border border-destructive/30 bg-destructive/10 flex items-center justify-center">
                  <WarningCircle weight="duotone" className="size-6 text-destructive" aria-hidden="true" />
                </div>
                <div className="text-center max-w-md">
                  <p className="text-base font-medium mb-1">Couldn&apos;t load this conversation</p>
                  <p className="text-sm text-muted-foreground/75">{sessionError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_24px_rgba(59,91,217,0.35)] transition-all hover:scale-[1.02]"
                >
                  Try again
                </button>
              </div>
            ) : isEvalMode && messages.length === 0 ? (
              /* Eval mode: show loading while autoSend fires */
              <div className="flex items-center justify-center py-12">
                <Loader />
              </div>
            ) : messages.length === 0 ? (
              /* Home View - Galexia OmniAgent prompt hub */
              <HomePromptHub
                key={input || 'home-empty'}
                initialInput={input}
                isPro={isPro}
                status={status}
                webSearch={webSearch}
                modelPill={composerModelPill}
                selectedMode={composerMode}
                onSelectMode={handleSelectComposerMode}
                onSubmit={handleSubmit}
                onStop={handleStop}
                onToggleWebSearch={handleHomeWebSearchToggle}
                onLockedMode={handleLockedHomeMode}
              />
            ) : (
              messages.map((message, messageIndex) => (
                <ChatMessageRow
                  key={message.id}
                  message={message}
                  isLastMessage={messageIndex === messages.length - 1}
                  prevIsAssistant={messages[messageIndex - 1]?.role === 'assistant'}
                  nextIsAssistant={messages[messageIndex + 1]?.role === 'assistant'}
                  status={status}
                  currentSessionId={currentSessionId}
                  readOnly={readOnly}
                  activeArtifact={activeArtifact}
                  isArtifactOpen={isArtifactOpen}
                  isDesktop={isDesktop}
                  activeDashboardId={activeDashboardId}
                  setActiveDashboardId={setActiveDashboardId}
                  openArtifact={openArtifact}
                  closeArtifact={closeArtifact}
                  handleSubmit={handleSubmit}
                  handleRendererContinue={handleRendererContinue}
                  handleRetryVizError={handleRetryVizError}
                  handleRetryFromFailure={handleRetryFromFailure}
                  regenerate={regenerate}
                  handleBranch={handleBranch}
                />
              ))
            )}

            {(status === 'submitted' || status === 'streaming') && <ActiveTurnIndicator />}

            {/* Chat error with retry — branded card matching the rest of
                the app's error language (visualizations list, 404, etc).
                role="alert" + aria-live so screen-reader users hear that
                the generation failed instead of just sitting on a silent
                stopped state. */}
            {chatError && status !== 'streaming' && status !== 'submitted' && (
              <ChatErrorPanel
                error={chatError}
                canRetryFromFailure={hasRecoverableTurnFailure}
                onRetry={() => {
                  setChatError(null);
                  if (hasRecoverableTurnFailure) {
                    handleRetryFromFailure();
                  } else {
                    regenerate();
                  }
                }}
                onDismiss={() => setChatError(null)}
              />
            )}
          </ConversationContent>
          <ConversationScrollButton />
        </Conversation>
      </div>

      )}

      {/* Input - Only show at bottom when in chat mode (has messages) and not readOnly/eval */}
      {!readOnly && !isEvalMode && messages.length > 0 && (
        <>
          {steeredNotices.length > 0 && (
            <div className="mx-auto flex w-full max-w-3xl flex-wrap gap-1.5 px-4 pb-1">
              {steeredNotices.map((notice, i) => (
                <span
                  key={`${i}-${notice.slice(0, 24)}`}
                  className="inline-flex max-w-full items-center gap-1.5 truncate rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-xs text-violet-300"
                  title={notice}
                >
                  <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-violet-400" aria-hidden />
                  Steering queued: {notice.length > 60 ? `${notice.slice(0, 59)}…` : notice}
                </span>
              ))}
            </div>
          )}
          <ChatComposer
            key={input || 'chat-empty'}
            initialInput={input}
            status={status}
            webSearch={webSearch}
            modelPill={composerModelPill}
            selectedMode={composerMode}
            onSelectMode={handleSelectComposerMode}
            onSubmit={handleSubmit}
            onStop={handleStop}
            onToggleWebSearch={handleHomeWebSearchToggle}
          />
        </>
      )}

      {/* Usage Limit alert — branded card matching the rest of the app.
          role="alert" + aria-live="assertive" so screen readers announce
          when a free user hits their daily cap; without these the toast
          was visually present but invisible to assistive tech. */}
      {!readOnly && !isEvalMode && usageLimitError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-28 left-1/2 -translate-x-1/2 w-full max-w-lg mx-auto px-4 z-50"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-4 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.5)] backdrop-blur-md">
            <div className="flex items-start gap-3">
              <div className="shrink-0 inline-flex size-10 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10">
                <WarningCircle weight="duotone" className="size-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground">Not enough Galexia credits</h4>
                <p className="text-sm text-muted-foreground/85 mt-1">
                  {usageLimitError.message}
                </p>
                <p className="text-xs text-muted-foreground/60 mt-1.5">
                  Monthly grants reset automatically. Add credits or choose a cheaper model.
                </p>
              </div>
              <button
                onClick={() => setUsageLimitError(null)}
                aria-label="Dismiss"
                className="shrink-0 size-7 inline-flex items-center justify-center rounded-md text-muted-foreground/55 hover:text-foreground hover:bg-muted/40 transition-colors"
              >
                <X weight="bold" className="size-4" />
              </button>
            </div>
            <div className="mt-3 flex gap-2">
              <a
                href="/pricing"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_4px_24px_rgba(59,91,217,0.35)] transition-all hover:scale-[1.02]"
              >
                Upgrade to Pro
              </a>
              <button
                onClick={() => setUsageLimitError(null)}
                className="px-4 py-2 text-sm font-medium text-muted-foreground/70 hover:text-foreground transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Auth-prompt modal — branded card matching the rest of the app */}
      {!readOnly && !isEvalMode && showAuthPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-md px-4"
          onClick={() => setShowAuthPrompt(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Sign in to send your question"
        >
          <motion.div
            ref={authDialogRef}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 0.61, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md p-8 border border-white/10 rounded-2xl bg-[#0B0B14] overflow-hidden"
            style={{
              boxShadow:
                '0 0 0 1px rgba(255,255,255,0.06), 0 32px 80px rgba(0,0,0,0.6), 0 0 100px rgba(139,92,246,0.12)',
            }}
          >
            {/* Aurora glow inside card */}
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-px rounded-2xl"
              style={{
                background:
                  'radial-gradient(ellipse at 30% 0%, rgba(139,92,246,0.18) 0%, transparent 60%), radial-gradient(ellipse at 70% 100%, rgba(6,182,212,0.10) 0%, transparent 60%)',
              }}
            />
            <button
              onClick={() => setShowAuthPrompt(false)}
              aria-label="Close"
              className="absolute top-3 right-3 z-10 inline-flex size-8 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/65 backdrop-blur-sm transition-all hover:border-white/20 hover:text-white"
            >
              <X weight="bold" className="size-4" />
            </button>
            <div className="relative text-center">
              <div
                className="inline-flex items-center justify-center size-14 rounded-2xl border border-violet-500/30 bg-violet-500/10 mb-5 mx-auto"
              >
                <LockSimple weight="duotone" className="size-7 text-violet-300" />
              </div>
              <h3
                className="font-normal mb-2 text-white"
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 24,
                  letterSpacing: -0.3,
                  lineHeight: 1.15,
                }}
              >
                One free account,{' '}
                <span
                  className="italic"
                  style={{
                    background: 'linear-gradient(135deg, #6B8AFF, #A78BFA, #38BDF8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  zero card.
                </span>
              </h3>
              <p className="text-sm text-white/65 mb-6 leading-relaxed">
                Sign in to keep your chats, files, media, and workflows together. No credit card required, cancel anytime.
              </p>
              <SignInButton mode="modal">
                <Button
                  size="lg"
                  className="w-full font-semibold bg-gradient-to-r from-[#3B5BD9] to-[#6B4FC7] text-white shadow-[0_4px_24px_rgba(59,91,217,0.4)] hover:shadow-[0_4px_32px_rgba(59,91,217,0.55)] hover:scale-[1.01] transition-all"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Continue
                  <ArrowsClockwise weight="bold" className="ml-1.5 size-4 hidden" />
                </Button>
              </SignInButton>
              <p className="mt-3 text-[11px] text-white/40">
                Your draft is saved — sign in and it&apos;ll be waiting.
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Composer voice-input + message-feedback toasts (permission denied,
          transcription failure, feedback sent, ...) — sonner's own portal,
          mounted once per page render regardless of which branch below
          returns this tree. */}
      <Toaster position="top-center" richColors closeButton />
    </div>
  );

  const artifactPanel = activeArtifact ? (
    <VisualizationArtifactPanel
      key={activeArtifact.key}
      toolName={activeArtifact.toolName}
      input={activeArtifact.input}
      output={activeArtifact.output}
      visualizationId={activeArtifact.visualizationId}
      sessionId={currentSessionId}
      onClose={closeArtifact}
      onRetryError={handleRetryVizError}
    />
  ) : null;

  // Page-level OpenUI Lang ThemeProvider. Without this, the chat library
  // renders against its built-in default tokens (off-brand greys), so the
  // chat-library Card / Tags / FollowUpBlock clash with Galexia's violet
  // surface. We resolve the default Galexia preset (galexia-violet, dark)
  // and ship those tokens to OpenUI so its components blend with the page.
  const labTheme = resolveTheme(null);
  const themed = (children: React.ReactNode) => (
    <ThemeProvider mode="dark" darkTheme={labTheme.darkTheme} lightTheme={labTheme.lightTheme}>
      {children}
    </ThemeProvider>
  );

  // Dashboard panel takes priority over the visualization artifact panel when
  // both could open — dashboards are the lab's flagship artifact (right
  // column persists across turns), and we don't want a one-off mermaid/svg
  // viz to evict an active dashboard from the right side.
  const dashboardPanelOpen = activeDashboardId !== null || streamingDashboard !== null;
  const dashboardPanel = dashboardPanelOpen ? (
    <DashboardPanel
      dashboardId={activeDashboardId}
      refreshKey={dashboardRefreshKey}
      streamingDashboard={streamingDashboard}
      adminView={adminView}
      onClose={() => {
        setActiveDashboardId(null);
        setStreamingDashboard(null);
      }}
      onSendInChat={(text) => {
        handleSubmit({ text, files: [] });
      }}
    />
  ) : null;

  if (isDesktop && dashboardPanelOpen && !codeMode) {
    return themed(
      <div className="h-full max-h-full overflow-hidden">
        <ResizablePanelGroup orientation="horizontal">
          <ResizablePanel defaultSize={25} minSize={20}>
            <div className="h-full overflow-hidden">{chatLayout}</div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={75} minSize={50}>
            <div className="h-full bg-background">{dashboardPanel}</div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  if (isDesktop && isArtifactOpen && activeArtifact && !codeMode) {
    return themed(
      <div className="h-full max-h-full overflow-hidden">
        <ResizablePanelGroup orientation="horizontal">
          <ResizablePanel defaultSize={35} minSize={25}>
            <div className="h-full overflow-hidden">{chatLayout}</div>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={65} minSize={35}>
            <div className="h-full border-l bg-background">{artifactPanel}</div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    );
  }

  return themed(
    <>
      <div className="h-full max-h-full overflow-hidden">{chatLayout}</div>
      {/* Mobile: dashboard (saved or streaming) slides in as a Sheet */}
      {dashboardPanelOpen && !isDesktop && (
        <Sheet open onOpenChange={(open) => {
          if (!open) {
            setActiveDashboardId(null);
            setStreamingDashboard(null);
          }
        }}>
          <SheetContent side="right" className="w-screen max-w-none p-0 sm:w-[88vw] sm:max-w-3xl" showCloseButton={false}>
            {dashboardPanel}
          </SheetContent>
        </Sheet>
      )}
      {activeArtifact && !dashboardPanelOpen && (
        <Sheet open={isArtifactOpen && !isDesktop} onOpenChange={setIsArtifactOpen}>
          <SheetContent side="right" className="w-screen max-w-none p-0 sm:w-[88vw] sm:max-w-3xl" showCloseButton={false}>
            {artifactPanel}
          </SheetContent>
        </Sheet>
      )}
      {/* First-visit-only welcome modal showing what Galexia produces. localStorage-gated. */}
      {!readOnly && !isEvalMode && <FirstVisitDemoModal onPrefill={(p) => setInput(p)} />}
    </>
  );
}
